import React, { useState, useRef, useEffect, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useNavigate, useLocation } from 'react-router-dom';
import { createLogger } from '../services/Debug';
import {
  FaPaperPlane,
  FaStop,
  FaCamera,
  FaFile,
  FaTrash,
  FaClipboardList,
  FaSpinner,
  FaTimes,
  FaMicrophone,
  FaPlay,
  FaQuestionCircle,
  FaPlane,
  FaUndo,
  FaCheckCircle,
  FaClipboard,
  FaRedo,
  FaSave,
} from 'react-icons/fa';
import { MdSignalWifiOff } from 'react-icons/md';

import './Recorder.css';
import './RecorderMobile.css';
import { getButtonClass } from './reporttype/reportType.js';

import Button from '../components/buttons/Button.js';
import ButtonRecord from '../components/buttons/ButtonRecord.js';
import UsageCircleView from './design/useCircle.js';
import Card from '../components/Card/Card.js';
import ReportTypeSelector from './reporttype/reportType.js';
import { Draft } from '../models/index.js';
import MicVisualizer from './recorders/MicVisualizer.js';
import { FaPencilAlt } from 'react-icons/fa';
import DraftsPanel from './draft/DraftsPanel.js';
import AnnexPanel from './annex/AnnexPannel.js';
import Notification from '../components/Notifications/Notification.js';
import useNotification from '../components/Notifications/useNotifications.js';
import { App as CapacitorApp } from '@capacitor/app';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { useWebRecorder } from './recorders/useWebRecorder.js';
import { useNetworkManager } from './networkManager.js';
import { useStatePersistence } from './statePersistence.js';
import { useDraftManager } from './draft/draftManager.js';
import { useRecordingControls } from './recorders/recordingControls.js';
import { useOcrProcessor } from './annex/ocrProcessor.js';
import { useAudioQueue } from './recorders/audioQueue.js';
import { writeToClipboard, tripleImpact, doubleLongTap } from './transcriptUtils.js';

import { isElectron, isCapacitor } from '../utils/platform.js';
import { openExternalUrl } from '../utils/browserHelper.js';
import { draftService, userService, reportTypeService, reportService, subscriptionService } from '../services/index.js';
import { airplaneDraftService } from '../services/AirplaneDraftService.js';
import { audioStorageService } from '../services/AudioStorageService.js';
// import Toggle removed – format toggle no longer used
import { BaseService } from '../services/BaseService.js';

import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { KeepAwake } from '@capacitor-community/keep-awake';

import SendSuccessAnimation from './design/SendSuccessAnimation.js';
import TutorialModal from '../components/TutorialModal/TutorialModal.js';
import ScreenshotPicker from './annex/ScreenshotPicker.js';
import ErrorDialog from '../components/ErrorDialog/ErrorDialog.js';
import FullScreenTextEditor from '../components/editors/FullScreenTextEditor.js';
import { useFullScreenTextEditor, useFullScreenTextarea } from '../hooks/useFullScreenTextEditor.js';
import { formatReportTypeDisplayName } from '../models/ReportTypeDesignHelper';
import HoverTooltip from '../components/tooltips/HoverTooltip.js';
import { useAuth } from '../context/AuthContext.js';
import LiveRecorder from './liveRecorder.js';


// ================== CONFIGS ==================
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const AIRPLANE_MODE_KEY = 'global_airplane_mode';

// Helper: promise timeout
const withTimeout = (promise, ms) => {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error('USAGE_CHECK_TIMEOUT')), ms);
  });
  return Promise.race([promise.finally(() => clearTimeout(timer)), timeout]);
};

// Success sound using Web Audio API (no external asset needed)
const playSuccessSound = () => {
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;
    const audioContext = new AudioContextClass();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    oscillator.type = 'sine';
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    const now = audioContext.currentTime;
    gainNode.gain.setValueAtTime(0.0001, now);
    gainNode.gain.exponentialRampToValueAtTime(0.2, now + 0.01);

    // Pleasant up-then-down confirmation chirp
    oscillator.frequency.setValueAtTime(880, now);
    oscillator.start(now);
    oscillator.frequency.exponentialRampToValueAtTime(1320, now + 0.12);
    oscillator.frequency.exponentialRampToValueAtTime(660, now + 0.28);

    gainNode.gain.exponentialRampToValueAtTime(0.0001, now + 0.35);
    oscillator.stop(now + 0.38);

    oscillator.onended = () => {
      try { audioContext.close(); } catch (_) {}
    };

    if (audioContext.state === 'suspended') {
      audioContext.resume().catch(() => {});
    }
  } catch (err) {
    const log = createLogger('playSuccessSound');
    log.warn('playSuccessSound error', err);
  }
};

// ================== LE COMPOSANT PRINCIPAL ==================
export default function RecorderQueueWithAnnex() {
  const log = createLogger('RecorderQueueWithAnnex');

  // ================== ÉTATS GLOBAUX ==================
  const { notification, showNotification, hideNotification } = useNotification();
  const { profile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // ======= Pour la sélection du template =======
  const [reportTypes, setReportTypes] = useState([]);
  const [reportTypeFavoritesModel, setReportTypeFavoritesModel] = useState(null);
  const [selectedReportType, setSelectedReportType] = useState('');
  const [showReportTypePanel, setShowReportTypePanel] = useState(false);
  const [hasUserChosenReportType, setHasUserChosenReportType] = useState(false);
  const [highlightReportType, setHighlightReportType] = useState(false);

  // ======= Pour l'enregistrement local / dictée =======
  const [isFakeRecording, setisFakeRecording] = useState(false);

  // ======= Pour le bouton envoyer =======
  const [isSendDisable, setIsSendDisable] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [showSendSuccess, setShowSendSuccess] = useState(false);

  //USAGE
  const [isGold, setIsGold] = useState(true);
  const [usageLeft, setUsageLeft] = useState(null);
  const [usageTotal, setUsageTotal] = useState(null);
  const [maxReports, setMaxReports] = useState(null);
  const [maxTranscriptions, setMaxTranscriptions] = useState(null);
  const [resetAt, setResetAt] = useState(null);
  const [planType, setPlanType] = useState('');
  const [isUsageLimitReached, setIsUsageLimitReached] = useState(false);

  // ======= Pour le texte principal + annexes =======
  const [transcript, setTranscript] = useState('');
  const [annexText, setAnnexText] = useState('');
  const textareaRef = useRef(null);

  // History for transcript appends (to enable undo of the last append)
  const appendHistoryRef = useRef([]);
  const lastTranscriptValueRef = useRef('');
  const [canUndoAppend, setCanUndoAppend] = useState(false);
  const undoingAppendRef = useRef(false);

  // Annexes (overlay)
  const [annexOpen, setAnnexOpen] = useState(false);
  const fileInputRef = useRef(null);

  // Auto-détection du template
  const [isDeterminingType, setIsDeterminingType] = useState(false);
  const [didAutoDetect, setDidAutoDetect] = useState(false);
  const [hasAutoDetectedForCurrentRecord, setHasAutoDetectedForCurrentRecord] = useState(false);

  // Brouillons (drafts)
  const [draftPanelOpen, setDraftPanelOpen] = useState(false);

  // Additional states
  const [showQueuePanel, setShowQueuePanel] = useState(false);
  const [transcriptionLimitExceeded, setTranscriptionLimitExceeded] = useState(false);
  const [transcriptionLimitDetails, setTranscriptionLimitDetails] = useState(null);

  // Tutorial states
  const [showTutorial, setShowTutorial] = useState(false);
  const [currentTutorial, setCurrentTutorial] = useState(null);
  const [hasShownOcrTutorial, setHasShownOcrTutorial] = useState(false);
  const [hasShownRecordingTutorial, setHasShownRecordingTutorial] = useState(false);

  // ETT entities support removed

  // ======= Pour le drag and drop =======
  const [isDragging, setIsDragging] = useState(false);
  const [isTranscribingMp3, setIsTranscribingMp3] = useState(false);

  // ======= Mode Live (Electron uniquement) =======
  const [isLiveMode, setIsLiveMode] = useState(false);
  const [isLiveProcessing, setIsLiveProcessing] = useState(false);
  const [toggleRecordShortcut, setToggleRecordShortcut] = useState('');
  const [liveOverlayText, setLiveOverlayText] = useState('');
  const [liveOverlayVisible, setLiveOverlayVisible] = useState(false);
  const liveOverlayTimerRef = useRef(null);
  const prevIsLiveModeRef = useRef(isLiveMode);
  const liveModeChangeByRendererRef = useRef(false);
  const liveFormatOnStopRef = useRef(false);
  const stopInitiatedBySendLiveRef = useRef(false);
  const formatPromptTimerRef = useRef(null);
  const [formatPromptActive, setFormatPromptActive] = useState(false);
  const [formatPromptProgress, setFormatPromptProgress] = useState(0);
  const formatPromptIntervalRef = useRef(null);
  const [formatPromptMsLeft, setFormatPromptMsLeft] = useState(500);

  const showLiveOverlay = useCallback((text) => {
    try {
      const trimmed = (text || '').trim();
      if (!trimmed) return;
      setLiveOverlayText(trimmed);
      // ensure transition plays on mount
      requestAnimationFrame(() => setLiveOverlayVisible(true));
      if (liveOverlayTimerRef.current) {
        clearTimeout(liveOverlayTimerRef.current);
      }
      liveOverlayTimerRef.current = setTimeout(() => {
        setLiveOverlayVisible(false);
        setTimeout(() => setLiveOverlayText(''), 280);
      }, 2000);
    } catch (_) {}
  }, []);

  useEffect(() => {
    return () => {
      if (liveOverlayTimerRef.current) {
        clearTimeout(liveOverlayTimerRef.current);
        liveOverlayTimerRef.current = null;
      }
      if (formatPromptTimerRef.current) {
        try { clearTimeout(formatPromptTimerRef.current); } catch (_) {}
        formatPromptTimerRef.current = null;
      }
      if (formatPromptIntervalRef.current) {
        try { clearInterval(formatPromptIntervalRef.current); } catch (_) {}
        formatPromptIntervalRef.current = null;
      }
    };
  }, []);

  // Sync Electron window size when toggling live mode
  useEffect(() => {
    if (!isElectron || !window?.electronAPI?.invoke) return;
    if (!liveModeChangeByRendererRef.current) return;
    try { window.electronAPI.invoke('recorder:mode', { live: isLiveMode }); } catch (_) {}
    finally { liveModeChangeByRendererRef.current = false; }
  }, [isLiveMode]);


  // Reflect live-state pushed from main process (e.g., when opened via shortcuts)
  useEffect(() => {
    if (!isElectron || !window?.electronAPI?.on) return;
    const remove = window.electronAPI.on('recorder:live-state', (_e, live) => {
      setIsLiveMode(!!live);
    });
    return () => { if (remove) try { remove(); } catch (_) {} };
  }, []);

  // Fetch recorder shortcut for hint text (Electron)
  useEffect(() => {
    if (!isElectron || !window?.electronAPI?.getShortcuts) return;
    (async () => {
      try {
        const map = await window.electronAPI.getShortcuts();
        setToggleRecordShortcut(map?.toggleRecord?.key || '');
      } catch (_) {}
    })();
  }, []);

  // When switching to live mode, auto-select a 'consultation' type if available
  useEffect(() => {
    if (!isLiveMode) return;
    try {
      const source = (Array.isArray(reportTypes) && reportTypes.length
        ? reportTypes
        : (profile?.reportTypeFavorites?.getFavorites?.() || [])) || [];
      const toLower = (s) => (s || '').toString().toLowerCase();
      const exact = source.find((n) => toLower(n) === 'consultation');
      const fuzzy = exact || source.find((n) => {
        const l = toLower(n);
        return l.includes('consultation') || l.includes('consutlation');
      });
      if (fuzzy) {
        setSelectedReportType(fuzzy);
        setHasUserChosenReportType(true);
      } else {
        setSelectedReportType('');
        setHasUserChosenReportType(false);
      }
    } catch (_) {
      setSelectedReportType('');
      setHasUserChosenReportType(false);
    }
  }, [isLiveMode, reportTypes, profile]);

  // Clear report type only when leaving live mode (avoid clearing on mount or unrelated updates)
  useEffect(() => {
    if (prevIsLiveModeRef.current && !isLiveMode) {
      setSelectedReportType('');
      setHasUserChosenReportType(false);
    }
    prevIsLiveModeRef.current = isLiveMode;
  }, [isLiveMode]);

  // Airplane mode prompt state
  const [showAirplanePrompt, setShowAirplanePrompt] = useState(false);
  const airplanePromptResolverRef = useRef(null);
  const askAirplaneMode = useCallback(() => {
    return new Promise((resolve) => {
      airplanePromptResolverRef.current = resolve;
      setShowAirplanePrompt(true);
    });
  }, []);

  // Global airplane mode (app-wide) - use state so UI updates when toggled
  const [isGlobalAirplaneMode, setIsGlobalAirplaneMode] = useState(() =>
    localStorage.getItem(AIRPLANE_MODE_KEY) === '1'
  );
  const lastAppliedAirplaneRef = useRef(null);

  // Airplane mode items state (audio + OCR saved locally)
  const [airplaneItems, setAirplaneItems] = useState([]);
  const [isProcessingAirplaneItem, setIsProcessingAirplaneItem] = useState(false);

  // ================== MICROPHONE AVAILABILITY CHECK ==================
  useEffect(() => {
    const checkMicAvailability = async () => {
      if (isCapacitor) return; // Skip on mobile
      try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
          alert('Aucun microphone détecté. Veuillez connecter un microphone et réessayer.');
          return;
        }
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasMic = devices.some((d) => d.kind === 'audioinput');
        if (!hasMic) {
          alert('Aucun microphone détecté. Veuillez connecter un microphone et réessayer.');
        }
      } catch (err) {
        log.error('[Recorder] Microphone check error:', err);
      }
    };
    checkMicAvailability();
  }, []);

  // ================== HOOK INTEGRATIONS ==================
  
  // Network management
  const { isOnline } = useNetworkManager();

  // Universal recorder - Web/Electron using new plugin
  const webRecorder = useWebRecorder(isGlobalAirplaneMode); // Disable VAD in airplane mode

  // For native platforms (iOS/Android), we'll use state managed by event listeners
  const [nativeRecordingState, setNativeRecordingState] = useState({
    isRecording: false,
    isPaused: false,
    voiceActivity: 0,
    isSilent: false
  });

  // Store native recording duration (from native plugin result)
  const nativeDurationRef = useRef(0);

  // Platform detection
  const isNative = isCapacitor;

  // Unified interface regardless of platform
  const isRecording = isNative ? nativeRecordingState.isRecording : webRecorder.isRecording;
  const isPaused = isNative ? nativeRecordingState.isPaused : webRecorder.isPaused;
  const voiceActivity = isNative ? nativeRecordingState.voiceActivity : webRecorder.voiceActivity;
  const isSilent = isNative ? nativeRecordingState.isSilent : webRecorder.isSilent;
  const recordingDuration = isNative ? nativeDurationRef.current : webRecorder.recordingDuration; // Native provides duration on stop

  // Unified methods
  const setIsRecording = isNative
    ? (value) => setNativeRecordingState(prev => ({ ...prev, isRecording: value }))
    : () => {}; // Web recorder manages its own state

  const setIsPaused = isNative
    ? (value) => setNativeRecordingState(prev => ({ ...prev, isPaused: value }))
    : () => {}; // Web recorder manages its own state

  const startRecording = isNative
    ? async () => {
        try {
          const { CapacitorM4aRecorder } = await import('capacitor-m4a-recorder');

          // Check and request microphone permission before recording (required for Android 6.0+)
          const permStatus = await CapacitorM4aRecorder.checkPermissions();
          if (permStatus.recordAudio !== 'granted') {
            const result = await CapacitorM4aRecorder.requestPermissions();
            if (result.recordAudio !== 'granted') {
              log.error('❌ Microphone permission denied');
              throw new Error('Microphone permission denied');
            }
          }

          await CapacitorM4aRecorder.startRecording({
            sampleRate: 44100,
            bitRate: 192000,
            enableVoiceActivityDetection: !isGlobalAirplaneMode,  // Disable VAD in airplane mode
            voiceActivityThreshold: -20, // Increased threshold: louder sounds now considered silent
            enableAutoPause: !isGlobalAirplaneMode,  // Disable auto-pause in airplane mode
            autoPauseDelay: 2000,
            autoResumeDelay: 0
          });
          nativeDurationRef.current = 0; // Reset duration on start
          setNativeRecordingState(prev => ({ ...prev, isRecording: true }));
          return true;
        } catch (error) {
          log.error('❌ Native recording error:', error);
          return false;
        }
      }
    : webRecorder.startRecording;

  const stopRecording = isNative
    ? async () => {
        try {
          const { CapacitorM4aRecorder } = await import('capacitor-m4a-recorder');
          const result = await CapacitorM4aRecorder.stopRecording();
          setNativeRecordingState({ isRecording: false, isPaused: false, voiceActivity: 0, isSilent: false });

          // Store the duration from native plugin (in milliseconds)
          if (result.duration && typeof result.duration === 'number') {
            nativeDurationRef.current = result.duration;
            log.info('✅ Native recording duration:', result.duration, 'ms');
          } else {
            nativeDurationRef.current = 0;
          }

          // Convert native file to Blob using fetch (memory efficient)
          if (result.uri) {
            const { Capacitor } = await import('@capacitor/core');
            const webPath = Capacitor.convertFileSrc(result.uri);
            const response = await fetch(webPath);
            const audioBlob = await response.blob();

            log.info('✅ Native recording blob created:', audioBlob.size, 'bytes');
            return audioBlob;
          }
          return new Blob([], { type: 'audio/m4a' });
        } catch (error) {
          log.error('❌ Error stopping native recording:', error);
          nativeDurationRef.current = 0;
          return new Blob([], { type: 'audio/m4a' });
        }
      }
    : webRecorder.stopRecording;

  const pauseRecording = isNative
    ? async () => {
        try {
          const { CapacitorM4aRecorder } = await import('capacitor-m4a-recorder');
          await CapacitorM4aRecorder.pauseRecording();
          setNativeRecordingState(prev => ({ ...prev, isPaused: true, isRecording: false }));
          log.info('⏸️ Native recording paused manually');
        } catch (error) {
          log.error('❌ Error pausing native recording:', error);
          throw error;
        }
      }
    : webRecorder.pauseRecording;

  const resumeRecording = isNative
    ? async () => {
        try {
          const { CapacitorM4aRecorder } = await import('capacitor-m4a-recorder');
          await CapacitorM4aRecorder.resumeRecording();
          setNativeRecordingState(prev => ({ ...prev, isPaused: false, isRecording: true }));
          log.info('▶️ Native recording resumed manually');
        } catch (error) {
          log.error('❌ Error resuming native recording:', error);
          throw error;
        }
      }
    : webRecorder.resumeRecording;

  const resetRecorder = isNative
    ? async () => {
        try {
          const { CapacitorM4aRecorder } = await import('capacitor-m4a-recorder');
          await CapacitorM4aRecorder.cancelRecording();
        } catch (error) {
          log.warn('Error cancelling native recording:', error);
        }
        nativeDurationRef.current = 0; // Reset duration on reset
        setNativeRecordingState({ isRecording: false, isPaused: false, voiceActivity: 0, isSilent: false });
      }
    : webRecorder.resetRecorder;

  // Set up native event listeners for iOS/Android
  useEffect(() => {
    if (!isNative) return;

    let voiceActivityListener;
    let autoPausedListener;
    let autoResumedListener;
    let errorListener;

    const setupNativeListeners = async () => {
      try {
        const { CapacitorM4aRecorder } = await import('capacitor-m4a-recorder');

        // Voice activity updates
        voiceActivityListener = await CapacitorM4aRecorder.addListener('voiceActivity', (event) => {
          setNativeRecordingState(prev => ({
            ...prev,
            voiceActivity: event.isVoiceDetected ? Math.abs(event.averagePower) : 0,
            isSilent: !event.isVoiceDetected
          }));
        });

        // Auto-pause event
        autoPausedListener = await CapacitorM4aRecorder.addListener('recordingAutoPaused', () => {
          log.info('📴 Native recording auto-paused');
          setNativeRecordingState(prev => ({ ...prev, isPaused: true }));
        });

        // Auto-resume event
        autoResumedListener = await CapacitorM4aRecorder.addListener('recordingAutoResumed', () => {
          log.info('▶️ Native recording auto-resumed');
          setNativeRecordingState(prev => ({ ...prev, isPaused: false }));
        });

        // Error event
        errorListener = await CapacitorM4aRecorder.addListener('recordingError', (event) => {
          log.error('❌ Native recording error:', event.message);
          setNativeRecordingState({ isRecording: false, isPaused: false, voiceActivity: 0, isSilent: false });
        });

      } catch (error) {
        log.error('Failed to setup native listeners:', error);
      }
    };

    setupNativeListeners();

    return () => {
      // Cleanup listeners
      voiceActivityListener?.remove();
      autoPausedListener?.remove();
      autoResumedListener?.remove();
      errorListener?.remove();
    };
  }, [isNative]);

  // Draft management
  const {
    drafts,
    currentDraftId,
    activeDrafts,
    sentDrafts,
    deletedDrafts,
    liveDrafts,
    isLoadingDrafts,
    draftError,
    loadDrafts,
    saveOrUpdateDraft,
    handleSelectDraft: draftHandleSelectDraft,
    handleDeleteDraft,
    handleDeleteAllDrafts,
    handleRestoreDeleted,
    moveCurrentDraftToSent,
    handleMoveToTrash: draftHandleMoveToTrash,
    setCurrentDraftId,
    handleBulkClearSection
  } = useDraftManager();
  
  // Update the ref with the save function
  useEffect(() => {
    saveOrUpdateDraftRef.current = saveOrUpdateDraft;
  }, [saveOrUpdateDraft]);

  // Setup fullscreen editor for Capacitor
  const {
    openEditor: openFullScreenEditor,
    editorProps: fullScreenEditorProps,
    isEnabled: isFullScreenEnabled
  } = useFullScreenTextEditor({
    title: 'Modifier l\'annexe',
    onSave: async (newText) => {
      setAnnexText(newText);
    },
    showCharCount: true,
    placeholder: 'Collez ou écrivez ici l\'annexe à joindre à votre rapport.'
  });

  // Setup fullscreen editor for main transcript textarea
  const {
    textareaProps: transcriptTextareaProps,
    editorProps: transcriptFullScreenEditorProps,
    isFullScreenEnabled: isTranscriptFullScreenEnabled
  } = useFullScreenTextarea({
    value: transcript,
    onChange: (newValue) => setTranscript(newValue),
    editorOptions: {
      title: 'Modifier la dictée',
      placeholder: '',
      showCharCount: true,
    }
  });

// For auto-detection we need this function first
const determineReportType = useCallback(async () => {
  // Check all guard conditions first
  if (didAutoDetect || hasUserChosenReportType || hasAutoDetectedForCurrentRecord || selectedReportType || isDeterminingType) {
    return;
  }
  
  const combinedLength = (transcript + annexText).trim().length;
  if (combinedLength < 10) {
    return;
  }

  
  try {
    setIsDeterminingType(true);
    // Mark that we're attempting auto-detection for this recording session
    setHasAutoDetectedForCurrentRecord(true);
    
    const favorites = profile?.reportTypeFavorites?.getFavorites?.() || [];
    // Build candidates with isEnterprise flag
    const sourceNames = (reportTypes && reportTypes.length > 0) ? reportTypes : favorites;
    const candidates = sourceNames.map(name => ({ name, isEnterprise: (reportTypeFavoritesModel?.isEnterprise?.(name) || profile?.reportTypeFavorites?.isEnterprise?.(name)) || false }));
    const suggestedType = await reportTypeService.determineReportType(transcript + annexText, candidates);
    
    if (suggestedType === 'inconnu' || suggestedType === 'not found') {
      if (hasUserChosenReportType) {
        return;
      }
      if (transcript.length < 1 && annexText.length < 1) {
        setHighlightReportType(false);
        setIsDeterminingType(false);
        return;
      }
      setHighlightReportType(true);
    } else if (suggestedType) {
      setDidAutoDetect(true);
      setSelectedReportType(suggestedType);
      setHighlightReportType(false);
      if (isCapacitor) {
        Haptics.impact({
          style: ImpactStyle.Light
        });
      }
    }
  } catch (err) {
    log.error('Auto-detect error =>', err);
    setHighlightReportType(false);
    setDidAutoDetect(false);
    // Reset the flag on error so it can retry
    setHasAutoDetectedForCurrentRecord(false);
  } finally {
    setIsDeterminingType(false);
  }
}, [
  transcript,
  annexText,
  didAutoDetect,
  hasUserChosenReportType,
  hasAutoDetectedForCurrentRecord,
  isDeterminingType,
  setHighlightReportType,
  setSelectedReportType,
  reportTypes,
  reportTypeFavoritesModel,
  selectedReportType
]);

  // Create a ref for the transcription limit exceeded callback
  const onTranscriptionLimitExceededRef = useRef(null);

  // Audio queue management
  const {
    queue,
    queueRef,
    processingRef,
    isTranscribing,
    queuePaused,
    manualAirplaneMode,
    pausedItems,
    queueWithMetadata,
    audioLibrary,
    addToQueue,
    processQueue,
    clearQueue,
    setQueuePaused,
    handlePauseItem,
    handleResumeItem,
    handleDeleteItem,
    handlePauseAll,
    handleResumeAll,
    handleDeleteAll,
    setQueue,
    setQueueWithMetadata,
    queueMetadataRef,
    addToQueueMetadata,
    errorDialogState,
    handleErrorRetry,
    handleErrorDelete,
    setErrorDialogState,
    retryTranscription,
    audioLibraryRef,
    clearAudioLibrary,
    deleteAudioFromLibrary,
    saveAudioLibraryForDraft,
    loadAudioLibraryForDraft,
    clearAudioLibraryForDraft
  } = useAudioQueue({
    isOnline: isOnline,
    showNotification,
    setTranscript,
    textareaRef,
    selectedReportType,
    getProcessedText: () => {
      // If we already have transcribed text, provide it as processedText
      return (transcript || '').trim();
    },
    onTranscriptionLimitExceeded: (details) => {
      if (onTranscriptionLimitExceededRef.current) {
        onTranscriptionLimitExceededRef.current(details);
      }
    },
    onTranscriptionError: null, // Will be handled by ErrorDialog component
    currentDraftId
  });

  // Respect global airplane mode: pause/resume queue based on global flag
  useEffect(() => {
    if (isGlobalAirplaneMode !== lastAppliedAirplaneRef.current) {
      lastAppliedAirplaneRef.current = isGlobalAirplaneMode;
      if (isGlobalAirplaneMode) {
        handlePauseAll();
      } else {
        // Sortie du mode avion
        handleResumeAll();

        // Si une dictée est en pause, la terminer et transcrire automatiquement
        if (isPaused) {
          log.info('🛬 Sortie du mode avion avec dictée en pause - arrêt et transcription automatique');

          // Terminer l'enregistrement en pause de manière asynchrone
          (async () => {
            try {
              // Arrêter l'enregistrement et obtenir le blob audio
              const audioBlob = await stopRecording();

              // Réinitialiser les états de recording
              if (isNative) {
                setNativeRecordingState({
                  isRecording: false,
                  isPaused: false,
                  voiceActivity: 0,
                  recordingDuration: 0
                });
              }

              // Vérifier que l'audio a du contenu
              if (audioBlob && audioBlob.size > 0) {
                const audioId = Date.now().toString();

                // Obtenir la durée d'enregistrement
                const durationMs = isNative && nativeDurationRef
                  ? nativeDurationRef.current
                  : webRecorder.recordingDuration;
                const durationSeconds = durationMs > 0 ? Math.round(durationMs / 1000) : undefined;

                // Ajouter à la queue de transcription
                addToQueue(audioBlob, audioId, durationSeconds ? { durationSeconds } : {});

                // Lancer la transcription après un court délai
                setTimeout(() => {
                  if (!processingRef.current && !queuePaused) {
                    processQueue();
                  }
                }, 100);

                showNotification('Dictée en pause terminée et ajoutée à la transcription', 'success');
              } else {
                log.warn('⚠️ Audio en pause vide, pas de transcription');
              }
            } catch (error) {
              log.error('❌ Erreur lors de l\'arrêt de la dictée en pause:', error);
              showNotification('Erreur lors de l\'arrêt de la dictée: ' + error.message, 'danger');
            }
          })();
        }
      }
    }
  }, [
    isGlobalAirplaneMode,
    handlePauseAll,
    handleResumeAll,
    showNotification,
    isPaused,
    isNative,
    stopRecording,
    nativeDurationRef,
    addToQueue,
    processQueue,
    processingRef,
    queuePaused,
    log,
    setNativeRecordingState,
    webRecorder.recordingDuration
  ]);


  
  // ================== TUTORIAL MANAGEMENT ==================
  const showTutorialMessage = useCallback((tutorialId, title, message) => {
    if (profile && profile.tutorials && !profile.tutorials.isCompleted(tutorialId)) {
      setCurrentTutorial({
        id: tutorialId,
        title,
        message,
        currentStep: tutorialId,
        totalSteps: 3
      });
      setShowTutorial(true);
    }
  }, []);

  const handleTutorialClose = useCallback(() => {
    setShowTutorial(false);
    setCurrentTutorial(null);
  }, []);

  const handleTutorialDontShowAgain = useCallback(async (tutorialId) => {
    try {
      // Update profile with completed tutorial
      await userService.updateProfile({
        tutorials: {
          [`tutorial_${tutorialId}`]: true
        }
      });
      
      // Update local profile cache
      if (profile && profile.tutorials) {
        profile.tutorials.complete(tutorialId);
        refreshProfile();
      }
    } catch (error) {
      log.error('Error marking tutorial as completed:', error);
    }
  }, []);


  // Recording controls with usage check
  const {
    triggerRecord: originalTriggerRecord,
    handleTranscribe,
    stopRecordingCompletely
  } = useRecordingControls({
    isRecording,
    setIsRecording,
    startRecording,
    stopRecording,
    pauseRecording,  // Platform-aware pause for airplane mode
    resumeRecording, // Platform-aware resume after pause
    addToQueue,
    processQueue,
    processingRef,
    queuePaused,
    manualAirplaneMode,
    setQueuePaused,
    showNotification,
    setIsSendDisable,
    setisFakeRecording,
    recordingDuration, // Pass duration from recorder hook
    nativeDurationRef, // Pass ref for native platforms to get latest value
    isNative, // Pass platform flag
    isPaused, // Pass pause state from parent (synced with nativeRecordingState)
    setIsPaused, // Pass setter for pause state
    onSegmentCaptured: async (audioSegment, meta) => {
      try {
        if (!(isElectron && isLiveMode)) return false;

        // Prepare file like audioQueue does
        let fileName = 'audio.webm';
        let fileType = audioSegment.type || 'audio/webm';
        const fileData = new File([audioSegment], fileName, { type: fileType });

          setIsLiveProcessing(true);
          const result = await reportService.sendLiveVoice(
            fileData,
            null,  // No annex in live mode
            { durationSeconds: meta?.durationSeconds }
          );
          const text = result.content || '';
          if (typeof window !== 'undefined' && window.electronAPI?.invoke) {
            await window.electronAPI.invoke('recorder:insert-text', text);
          }
          try { if ((text || '').trim()) { await writeToClipboard(text); } } catch (_) {}
          showLiveOverlay(text);
          setIsLiveProcessing(false);
          handleClearInterface(true);
          return true;
        } catch (err) {
        log.error('Live mode segment error:', err);
        showNotification('Erreur live', 'danger');
        setIsLiveProcessing(false);
        return true; // prevent queue fallback
      }
    },
    onStopRecordingComplete: async (finalAudio, meta) => {
      try {
        // Live mode logic
        if (isElectron && isLiveMode) {
          // Prepare File object consistently with audioQueue processing
          let fileName = 'audio.webm';
          let fileType = finalAudio.type || 'audio/webm';
          if (isCapacitor) {
            fileName = 'audio.wav';
            fileType = 'audio/wav';
          } else if (finalAudio.type) {
            if (finalAudio.type.includes('webm')) {
              fileName = 'audio.webm';
            } else if (finalAudio.type.includes('wav')) {
              fileName = 'audio.wav';
            } else if (finalAudio.type.includes('aac')) {
              fileName = 'audio.aac';
            }
          }
          const fileData = new File([finalAudio], fileName, { type: fileType });
          // Always send to live-voice endpoint regardless of selected report type
          
          try {
            // If stop was initiated by the explicit Send Live button, skip this auto-send
            if (stopInitiatedBySendLiveRef.current) {
              return true;
            }
            setIsLiveProcessing(true);
            const result = await reportService.sendLiveVoice(
              fileData,
              null,  // No annex in live mode
              { durationSeconds: meta?.durationSeconds, shouldMettreEnForme: !!liveFormatOnStopRef.current }
            );
            const text = result.content || '';
            if (typeof window !== 'undefined' && window.electronAPI?.invoke) {
              try { if ((text || '').trim()) { await writeToClipboard(text); } } catch (_) {}
              await window.electronAPI.invoke('recorder:insert-text', text);
            }
            showLiveOverlay(text);
          } catch (err) {
            log.error('Live voice send error:', err);
            showNotification('Erreur lors de l\'envoi live', 'danger');
          } finally {
            setIsLiveProcessing(false);
            handleClearInterface(true);
            // Reset prompt state after completion
            liveFormatOnStopRef.current = false;
          }
          return true;
        }
      } catch (err) {
        log.error('Live mode transcription error:', err);
        showNotification('Erreur de transcription en mode Live', 'danger');
        return true; // prevent double handling even on error
      }
      return false; // not handled, continue normal flow
    }
  });

  // ================== USAGE CHECK (BACKGROUND) ==================
  // Non-blocking usage check that runs in background after recording starts.
  // If limits are exceeded while recording, stops the recording.
  const checkUsageBeforeRecord = useCallback(async () => {
    // Skip check if offline or airplane mode is active
    if (!isOnline || isGlobalAirplaneMode) {
      return;
    }

    try {
      const usagePromise = subscriptionService.getUsageQuickCheck();
      // On Capacitor, enforce 5s timeout
      const usageData = isCapacitor
        ? await withTimeout(usagePromise, 5000)
        : await usagePromise;

      const planType = usageData.planType;
      const isGoldPlan = planType === 'gold' || planType === 'enterprise';
      setIsGold(isGoldPlan);
      setPlanType(planType);

      // Redirect wood plan users to subscription required page
      if (planType === 'wood') {
        if (isRecording || isFakeRecording) {
          await stopRecordingCompletely();
        }
        navigate('/subscription-required', { replace: true });
        return;
      }

      setResetAt(usageData.resetAt);

      const maxReports = usageData.maxReports || 0;
      const maxTranscriptions = usageData.maxTranscriptions || 0;
      const reportsToday = usageData.reportsToday || 0;
      const transcriptionsToday = usageData.transcriptionsToday || 0;

      setMaxReports(maxReports);
      setMaxTranscriptions(maxTranscriptions);

      const reportsLeft = Math.max(0, maxReports - reportsToday);

      setUsageLeft(reportsLeft);
      setUsageTotal(maxReports);

      // Check limits for ALL plans (including gold/enterprise)
      const limitsExceeded = reportsToday >= maxReports ||
                            transcriptionsToday >= maxTranscriptions;
      setIsUsageLimitReached(limitsExceeded);

      // If limits exceeded and currently recording, stop the recording
      if (limitsExceeded && (isRecording || isFakeRecording)) {
        await stopRecordingCompletely();
      }
    } catch (error) {
      // Silent fail - don't block recording on error
      log.error('Usage check error (background):', error.message);
    }
  }, [isOnline, isGlobalAirplaneMode, isCapacitor, isRecording, isFakeRecording, stopRecordingCompletely, navigate]);

  // Wrap triggerRecord - recording starts immediately, usage check runs in background
  const triggerRecord = useCallback(async () => {
    log.debug('triggerRecord, isFakeRecording, isLiveMode, isRecording', isFakeRecording, isLiveMode, isRecording);

    // Show tutorial for first-time recording (before actually starting the recording)
    if (!isRecording && !isFakeRecording && !hasShownRecordingTutorial && !isLiveMode) {
      if (profile && profile.tutorials && !profile.tutorials.isCompleted(4)) {
        setHasShownRecordingTutorial(true);
        showTutorialMessage(
          4,
          'Comment dicter efficacement',
          "Parlez naturellement, il n'est pas nécessaire de dicter la ponctuation. Commencez par dire votre modèle de rapport, par exemple \"je dicte une consultation, le motif c'est ...\". Evitez les écouteurs Bluetooth qui diminuent la qualité de la transcription."
        );
        return; // Show tutorial first, user will need to press record again
      }
    }

    // If already recording (pause/resume toggle), proceed directly
    if (isRecording || isFakeRecording) {
      await originalTriggerRecord();
      return;
    }

    // Check if already blocked (from previous background check)
    if (isUsageLimitReached || transcriptionLimitExceeded) {
      // Limits exceeded - modal will be shown via isUsageLimitReached state
      return;
    }

    // Reset the auto-detection flag for new recording
    setHasAutoDetectedForCurrentRecord(false);

    // Start recording IMMEDIATELY
    await originalTriggerRecord();

    // Check usage in BACKGROUND (non-blocking)
    checkUsageBeforeRecord();
  }, [isRecording, isFakeRecording, hasShownRecordingTutorial, isLiveMode, profile, showTutorialMessage,
      originalTriggerRecord, isUsageLimitReached, transcriptionLimitExceeded, checkUsageBeforeRecord]);

  // OCR processing
  const {
    ocrQueue,           // Array of File objects for airplane mode save
    ocrQueueLength,
    isExtractingText,
    addFileToOcrQueue,
    processOcrQueue,    // Trigger OCR processing (for resuming after airplane mode)
    clearOcrQueue,
    ocrErrorDialogState,
    handleOcrErrorRetry,
    handleOcrErrorDelete,
    setOcrErrorDialogState,
    processingProgress,
    saveOcrQueueForDraft,
    loadOcrQueueForDraft,
    clearOcrQueueForDraft
  } = useOcrProcessor({
    isOnline: isOnline,
    showNotification,
    setAnnexText,
    selectedReportType,
    currentDraftId,
    isGlobalAirplaneMode,
    onOcrComplete: useCallback(() => {
      // Tutorial 2: Show message about using extracted text only once per session
      if (!hasShownOcrTutorial) {
        setHasShownOcrTutorial(true);
        showTutorialMessage(
          2,
          'Texte extrait avec succès',
          'Vous pouvez parler du scan que vous avez fait comme de l\'annexe à voxia pour l\'inclure dans votre rapport (par exemple "écris ici la liste des médicaments en annexe").'
        );
      }
    }, [hasShownOcrTutorial, showTutorialMessage, selectedReportType]),
    onOcrError: null
  });

  // Append annex text from global shortcut (Electron)
  useEffect(() => {
    if (!window.electronAPI) return;
    const onAppend = (_evt, txt) => {
      if (!txt || typeof txt !== 'string') return;
      setAnnexText(prev => {
        const prefix = prev && prev.trim() ? `${prev}\nProchain annexe:\n` : 'Prochain annexe:\n';
        return `${prefix}${txt}\n`;
      });
    };
    const remove = window.electronAPI.on('annex:append-text', onAppend);
    return () => { if (remove) remove(); };
  }, [setAnnexText]);

  // ────────────────────────────────────────────────────────────
  // Handle screenshot OCR from global shortcuts (Electron)
  // ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!window.electronAPI) return;

    const handleScreenshotOCR = (event, dataUrl) => {
      if (dataUrl) {
        // Convert base64 dataUrl to a File object
        const byteString = atob(dataUrl.split(',')[1]);
        const mimeString = dataUrl.split(',')[0].split(':')[1].split(';')[0];
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
        }
        const blob = new Blob([ab], { type: mimeString });
        const file = new File([blob], `screenshot-${Date.now()}.png`, { type: mimeString });
        addFileToOcrQueue(file);
      } else {
        log.error('[LiveRecorder] No screenshot data received from global shortcut');
      }
    };

    const removeListener = window.electronAPI.on('process-screenshot-ocr', handleScreenshotOCR);

    return () => {
      if (removeListener) removeListener();
    };
  }, [addFileToOcrQueue]);

  // Legacy toggle live listener removed in favor of unified shortcut

  // Toggle formatting via global shortcut (Electron) only when visible AND in live mode
  // Live format toggle removed

  // Handle transcription limit exceeded
  const handleTranscriptionLimitExceeded = useCallback(async (details) => {
    log.error('🚫 Transcription limit exceeded, stopping all recording activities');
    
    // Set the limit exceeded state
    setTranscriptionLimitExceeded(true);
    setTranscriptionLimitDetails(details);
    
    // Stop any active recording
    if (isRecording) {
      await stopRecordingCompletely();
    }
    
    // Clear any pending items
    clearQueue();
    
    // Update usage display
    setUsageLeft(0);
    
    // Double long tap for haptic feedback
    doubleLongTap();
  }, [isRecording, stopRecordingCompletely, clearQueue]);

  // Set the ref
  useEffect(() => {
    onTranscriptionLimitExceededRef.current = handleTranscriptionLimitExceeded;
  }, [handleTranscriptionLimitExceeded]);

  // Refs to hold current values for navigation effect
  const transcriptRef = useRef(transcript);
  const annexTextRef = useRef(annexText);
  const selectedReportTypeRef = useRef(selectedReportType);
  const saveOrUpdateDraftRef = useRef(null);
  
  // Update refs when values change
  useEffect(() => {
    transcriptRef.current = transcript;
  }, [transcript]);

  // Detect and record append operations on transcript
  useEffect(() => {
    const previous = lastTranscriptValueRef.current || '';
    const current = transcript || '';
    if (undoingAppendRef.current) {
      // We triggered this change via undo; do not alter history
      undoingAppendRef.current = false;
      lastTranscriptValueRef.current = current;
      return;
    }
    if (current.length > previous.length && current.startsWith(previous)) {
      // An append at the end occurred; record previous value for undo
      try {
        appendHistoryRef.current.push(previous);
        setCanUndoAppend(true);
      } catch (_) {}
    } else if (current !== previous) {
      // Non-append modification; reset history to avoid incorrect undo
      appendHistoryRef.current = [];
      setCanUndoAppend(false);
    }
    lastTranscriptValueRef.current = current;
  }, [transcript]);

  const handleUndoAppend = useCallback(() => {
    try {
      const history = appendHistoryRef.current || [];
      if (!history.length) return;
      const prevValue = history.pop();
      undoingAppendRef.current = true;
      lastTranscriptValueRef.current = prevValue;
      setTranscript(prevValue);
      setCanUndoAppend(history.length > 0);
    } catch (_) {}
  }, [setTranscript]);
  
  useEffect(() => {
    annexTextRef.current = annexText;
  }, [annexText]);
  
  useEffect(() => {
    selectedReportTypeRef.current = selectedReportType;
  }, [selectedReportType]);

  // State persistence
  const { clearTemporaryState, setIsClearing, saveDraftIfNeeded } = useStatePersistence({
    transcript,
    annexText,
    isFakeRecording,
    selectedReportType,
    isRecording,
    highlightReportType,
    queueRef,
    setTranscript,
    setAnnexText,
    setisFakeRecording,
    setSelectedReportType,
    setHighlightReportType,
    setQueue,
    setQueueWithMetadata,
    queueMetadataRef,
    processQueue,
    processingRef,
    saveOrUpdateDraft
  });
  
  // Effect to save draft ONLY when actually navigating away
  useEffect(() => {
    // Store the initial mount state
    let isMounted = true;
    
    return () => {
      // Only save if we're actually unmounting (navigating away)
      if (isMounted) {
        isMounted = false;
        const hasContent = transcriptRef.current?.trim() || annexTextRef.current?.trim();
        if (hasContent && saveOrUpdateDraftRef.current) {
          // Use refs for all values to avoid stale closures
          saveOrUpdateDraftRef.current(
            transcriptRef.current, 
            annexTextRef.current, 
            selectedReportTypeRef.current
          );
        }
      }
    };
  }, []); // Empty dependency array - only run on mount/unmount

  // ================== EFFETS INITIAUX ==================
  
  
  useEffect(() => {
    if (!isCapacitor) {return;}
    // Active Keep Awake quand la vue est montée
    const activateKeepAwake = async () => {
      const supported = await KeepAwake.isSupported();
      if (supported.isSupported) {
        await KeepAwake.keepAwake();
        
      } else {
        log.warn('Keep Awake non supporté sur cet appareil.');
      }
    };

    // Désactive Keep Awake quand la vue est démontée
    const deactivateKeepAwake = async () => {
      const keptAwake = await KeepAwake.isKeptAwake();
      if (keptAwake.isKeptAwake) {
        await KeepAwake.allowSleep();
        
      }
    };

    activateKeepAwake();

    // Nettoyage (désactivation automatique)
    return () => {
      deactivateKeepAwake();
    };
  }, []);

  // ================== LIVE SEND WITH FORMATTING ==================
  const handleSendLive = useCallback(async () => {
    if (isSending) return;

    try {
      setIsSending(true);
      // Mimic double-tap shortcut: mark for formatting on stop, then stop recording
      liveFormatOnStopRef.current = true;

      // Stop recording if it's active (this triggers onStopRecordingComplete which will send with formatting)
      if (isRecording || isFakeRecording) {
        await stopRecordingCompletely();
        // Small delay to ensure the recording fully stops before leaving
        await new Promise(resolve => setTimeout(resolve, 100));
      }

    } catch (err) {
      log.error('Send Live error:', err);
      showNotification('Erreur lors de l\'envoi live', 'danger');
    } finally {
      setIsSending(false);
    }
  }, [isSending, isRecording, isFakeRecording, stopRecordingCompletely, showNotification]);

  // ================== STOP RECORDING AND SEND LIVE ==================
  const handleStopAndSendLive = useCallback(async () => {
    // Stop recording if active; onStopRecordingComplete will handle unformatted send
    if (isRecording || isFakeRecording) {
      await stopRecordingCompletely();
      // Small delay to ensure recording is fully stopped
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }, [isRecording, isFakeRecording, stopRecordingCompletely]);

  // Handle global toggle-recording from Electron
  useEffect(() => {
    if (!window.electronAPI) return;
    const handler = () => {
      if (isRecording) {
        // If currently recording, stop completely
        stopRecordingCompletely();
      } else {
        // Start a new recording sequence (respects usage checks in triggerRecord)
        triggerRecord();
      }
    };
    const remove = window.electronAPI.on('toggle-recording', handler);
    return () => { if (remove) remove(); };
  }, [isRecording, triggerRecord, stopRecordingCompletely]);

  // Unified live record shortcut: ensure listener is declared after handleStopAndSendLive exists
  useEffect(() => {
    if (!window.electronAPI) return;
    const handler = async () => {
      if (isRecording || isFakeRecording) {
        // If recording but not yet in live mode, only switch to live (main handles it) and do not stop
        if (!isLiveMode) return;
        // If already live and recording, start 1s format prompt: second tap formats, otherwise plain
        if (!formatPromptActive) {
          try { if (formatPromptTimerRef.current) clearTimeout(formatPromptTimerRef.current); } catch (_) {}
          try { if (formatPromptIntervalRef.current) clearInterval(formatPromptIntervalRef.current); } catch (_) {}
          setFormatPromptActive(true);
          setFormatPromptProgress(0);
          setFormatPromptMsLeft(500);
          const start = Date.now();
          const duration = 500;
          const end = start + duration;
          formatPromptIntervalRef.current = setInterval(() => {
            const now = Date.now();
            const left = Math.max(0, end - now);
            setFormatPromptMsLeft(left);
            const p = Math.min(100, Math.max(0, 100 - (left / duration) * 100));
            setFormatPromptProgress(p);
            if (left <= 0) {
              try { clearInterval(formatPromptIntervalRef.current); } catch (_) {}
              formatPromptIntervalRef.current = null;
            }
          }, 33);
          formatPromptTimerRef.current = setTimeout(async () => {
            setFormatPromptActive(false);
            liveFormatOnStopRef.current = false;
            await handleStopAndSendLive();
          }, 500);
          return;
        }
        // Second tap within the window → stop with formatting
        try { if (formatPromptTimerRef.current) clearTimeout(formatPromptTimerRef.current); } catch (_) {}
        try { if (formatPromptIntervalRef.current) clearInterval(formatPromptIntervalRef.current); } catch (_) {}
        setFormatPromptActive(false);
        liveFormatOnStopRef.current = true;
        await handleStopAndSendLive();
      } else {
        // Not recording → start recording (after main switched to live if needed)
        await triggerRecord();
      }
    };
    const remove = window.electronAPI.on('toggle-live-record', handler);
    return () => { if (remove) remove(); };
  }, [isRecording, isFakeRecording, isLiveMode, triggerRecord, handleStopAndSendLive, formatPromptActive]);

  const refreshReportTypes = async () => {
    try {
      const reportTypesData = await reportTypeService.getFavorites();
      setReportTypeFavoritesModel(reportTypesData);
      setReportTypes(reportTypesData.getFavorites());
  
     
    } catch (err) {
      log.error('Erreur loading report types:', err.message);
      // Fallback to profile favorites
      try {
        if (profile?.reportTypeFavorites) {
          setReportTypeFavoritesModel(profile.reportTypeFavorites);
          setReportTypes(profile.reportTypeFavorites.getFavorites());
        } else {
          setReportTypeFavoritesModel(null);
          setReportTypes([]);
        }
      } catch (_) {
        setReportTypeFavoritesModel(null);
        setReportTypes([]);
      }
    }
  };

  useEffect(() => {
    refreshReportTypes();
  }, []);

  const handleToggleReportTypePanel = useCallback(() => {
    setShowReportTypePanel(prev => {
      if (!prev) {
        // Opening the panel - refresh report types
        refreshReportTypes();
      }
      return !prev;
    });
  }, []);

  // Initial usage check on mount to populate UI (UsageCircleView)
  useEffect(() => {
    checkUsageBeforeRecord();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen to global usage limit changes from BaseService
  useEffect(() => {
    const unsubscribe = BaseService.addUsageLimitListener((isReached, details) => {

      
      if (isReached) {
        setIsUsageLimitReached(true);
        if (details?.resetAt) {
          setResetAt(details.resetAt);
        }
        
        // Stop any active recording if limit is reached
        if (isRecording) {
          stopRecordingCompletely();
        }
        
   
        
        // Double long tap for haptic feedback
        doubleLongTap();
      }
    });
    
    return unsubscribe;
  }, [isRecording, stopRecordingCompletely, showNotification]);

  // Check if transcription limit reset time has passed
  useEffect(() => {
    if (!transcriptionLimitExceeded || !transcriptionLimitDetails) return;

    const checkResetTime = () => {
      const resetTime = new Date(transcriptionLimitDetails.resetAt);
      const now = new Date();
      
      if (now >= resetTime) {

        setTranscriptionLimitExceeded(false);
        setTranscriptionLimitDetails(null);
        // Refresh usage data with forced check
        checkUsageBeforeRecord();
      }
    };

    // Check immediately
    checkResetTime();

    // Check every minute
    const interval = setInterval(checkResetTime, 60000);

    return () => clearInterval(interval);
  }, [transcriptionLimitExceeded, transcriptionLimitDetails, checkUsageBeforeRecord]);


  useEffect(() => {
    if (selectedReportType) return;
    if (transcript.length > 10 || annexText.length > 10) {
      determineReportType();
    }
  }, [transcript, annexText, selectedReportType, determineReportType]);


  const handleSelectReportType = useCallback((typeName) => {
    setSelectedReportType(typeName);
    setHasUserChosenReportType(true);
    setHighlightReportType(false);
    setIsDeterminingType(false);
    setShowReportTypePanel(false);
  }, [hasUserChosenReportType, isDeterminingType, setHighlightReportType, setIsDeterminingType, setSelectedReportType]);





  // ================== CHARGEMENT DES ITEMS MODE AVION ==================
  // Load airplane items on mount and when airplane mode changes
  useEffect(() => {
    const loadAirplaneItems = () => {
      try {
        const items = airplaneDraftService.getAllItems();
        setAirplaneItems(items);
        log.info('[Recorder] Loaded', items.length, 'airplane items');
      } catch (error) {
        log.error('[Recorder] Error loading airplane items:', error);
      }
    };
    loadAirplaneItems();
    // Cleanup expired items in IndexedDB
    audioStorageService.cleanupExpiredAirplaneItems().catch(() => {});
  }, []);

  // ================== ENVOI DU RAPPORT ==================
const handleSend = async () => {
  if (!isOnline) {
    // Save as draft when offline
    const finalText = transcript.trim();
    const finalAnnex = annexText.trim();

    if (finalText.length > 50000) {
      alert('La dictée est trop longue, max 50000 caractères.');
      return;
    }
    if (finalAnnex.length > 100000) {
      alert('L\'annexe est trop longue, max 100000 caractères.');
      return;
    }
    // Save as draft
    await saveOrUpdateDraft(finalText, finalAnnex, selectedReportType || 'texte', 'draft');
    
    showNotification('Rapport sauvegardé en brouillon. Envoyez-le une fois en ligne.', 'info');
    handleClearInterface();
    return;
  }
  
  if (isSending) return;
  if (isRecording) {
    alert('Enregistrement encore en cours. Veuillez arrêter la dictée avant.');
    return;
  }
  
  // Check if all is transcribed
  if (queueRef.current.length > 0) {
    alert('Transcription encore en cours. Veuillez patienter.');
    return;
  }

  if (!selectedReportType) {
    alert('Veuillez sélectionner un modèle.');
    return;
  }

   
   // Check if we should show the tutorial
   if (profile && profile.tutorials && !profile.tutorials.isCompleted(1)) {
     showTutorialMessage(
       1,
       'Modèle identifié',
       `voxia a identifié "${formatReportTypeDisplayName(selectedReportType)}" comme modèle de votre dictée. Si ce n'est pas correct, touchez le bouton "Modèle" en haut à gauche pour changer.`
     );
     return;
   }


  const finalText = transcript.trim();
  const finalAnnex = annexText.trim();
  if (!finalText && !finalAnnex) return;

  try {
    setIsSending(true);

    // ETT entities formatting removed; use annex as-is
    const formattedAnnex = finalAnnex;

    // Save current state as draft before sending
    await saveOrUpdateDraft(finalText, formattedAnnex, selectedReportType, 'sent');

    // Generate and send report
    const report = await reportService.generateReport(
      { name: selectedReportType, isEnterprise: (reportTypeFavoritesModel?.isEnterprise?.(selectedReportType) || profile?.reportTypeFavorites?.isEnterprise?.(selectedReportType)) || false },
      finalText,
      formattedAnnex
    );
    
    // Move draft to sent status after successful send
    if (report) {
      await moveCurrentDraftToSent(finalText, finalAnnex);

      // Clear audio library and OCR queue for the sent draft
      if (currentDraftId) {
        try {
          clearAudioLibraryForDraft(currentDraftId);
          clearOcrQueueForDraft(currentDraftId);
        } catch (e) {
          log.error('Failed to clear audio/OCR data for sent draft:', e);
        }
      }

      // Show success animation
      setShowSendSuccess(true);
      tripleImpact();
      // Play success sound mid-animation (~1s into a 2s animation)
      setTimeout(() => {
        try { playSuccessSound(); } catch (_) {}
      }, 1000);

      // Clear interface after delay
      setTimeout(async () => {
        await handleClearInterface();
        // Force refresh usage data after sending report
        await checkUsageBeforeRecord();

        showTutorialMessage(
          3,
          'Rapport envoyé avec succès',
          `Votre ${formatReportTypeDisplayName(selectedReportType)} sera prêt dans quelques secondes sur notre site web https://voxia-md.ch".`
        );
      }, 500);
    } else {
      showNotification("Erreur lors de l'envoi du rapport. Réessayez.", 'danger');
      doubleLongTap();
    }
  } catch (err) {
    log.error('Send error =>', err);
    showNotification(`Erreur dans l'envoi du rapport : ${err.message}`, 'danger');
    doubleLongTap();
  } finally {
    setIsSending(false);
  }
};


  // ================== GESTION DES ANNEXES (fichiers/texte) ==================
  const handleToggleAnnex = () => {
    // Always open the annex panel overlay, regardless of platform
    setAnnexOpen((prev) => !prev);
  };
  const handleClearAnnex = () => setAnnexText('');
  const handlePasteAnnex = async () => {
    try {
      const clip = await navigator.clipboard.readText();
      if (!annexText.trim()) {
        setAnnexText(`Prochain annexe:\n${clip}\n`);
      } else {
        setAnnexText((prev) => `${prev}\nProchain annexe:\n${clip}\n`);
      }
    } catch (err) {
      log.error(err);
    }
  };

  async function handleTakePhoto() {
    if (isCapacitor) {
      const permissions = await Camera.checkPermissions();
      if (permissions.camera !== 'granted') {
        const permissionResult = await Camera.requestPermissions();
        if (permissionResult.camera !== 'granted') {
          showNotification('Permission caméra requise pour prendre une photo', 'warning');
          return;
        }
      }
      try {
        const image = await Camera.getPhoto({
          quality: 90,
          resultType: CameraResultType.Base64,
          source: CameraSource.Camera,
        });
        const base64Data = image.base64String;

        // Décoder la base64 pour créer un vrai fichier binaire
        const raw = atob(base64Data);  
        const rawLength = raw.length;
        const arr = new Uint8Array(rawLength);
        for (let i = 0; i < rawLength; i++) {
          arr[i] = raw.charCodeAt(i);
        }

        const file = new File([arr], `${uuidv4()}.jpg`, { type: 'image/jpeg' });
        onFileChange({ target: { files: [file] } });
      } catch (err) {
        log.error('handleTakePhoto =>', err);
      }
    } else {
      if (fileInputRef.current) {
        fileInputRef.current.click(); 
      }
    }
  }

  const handleSelectFromLibrary = async () => {
    if (isCapacitor) {
      try {
        const image = await Camera.getPhoto({
          quality: 90,
          resultType: CameraResultType.Base64,
          source: CameraSource.Photos,
        });
        const base64Data = image.base64String;
        
        // Décoder la base64 pour créer un vrai fichier binaire (comme pour la camera)
        const raw = atob(base64Data);  
        const rawLength = raw.length;
        const arr = new Uint8Array(rawLength);
        for (let i = 0; i < rawLength; i++) {
          arr[i] = raw.charCodeAt(i);
        }
        
        const file = new File([arr], `${uuidv4()}.jpg`, { type: "image/jpeg" });
        onFileChange({ target: { files: [file] } });
      } catch (err) {
        log.error('handleSelectFromLibrary =>', err);
      }
    } else {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    }
  };

  const onFileChange = useCallback(async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
  
    // Process all selected files
    const fileArray = Array.from(files);
    
    // On efface la sélection du <input type="file">
    e.target.value = '';
  
    // Add all files to the OCR queue
    fileArray.forEach(file => {
      addFileToOcrQueue(file);
    });
  }, [addFileToOcrQueue, showNotification]);

  // ================== GESTION DES BROUILLONS ==================
  useEffect(() => {
    if (!window.electronAPI) return;
  
    const handleAppStateChange = async (event, state) => {
      
      
      // Only save draft when app is about to quit
      if (state === 'before-quit') {
        const hasContent = transcript?.trim() || annexText?.trim();
        if (hasContent) {
          
          await saveOrUpdateDraft(transcript, annexText, selectedReportType);
        }
      }
    };
  
    const removeListener = window.electronAPI.on('app-state-change', handleAppStateChange);
  
    return () => {
      if (removeListener) removeListener();
    };
  }, [transcript, annexText, selectedReportType, saveOrUpdateDraft]);

  // Save draft when recorder window is about to hide
  useEffect(() => {
    if (!window.electronAPI) return;

    const handleRecorderWindowHide = async () => {
      
      const hasContent = transcript?.trim() || annexText?.trim();
      if (hasContent) {
        
        await saveOrUpdateDraft(transcript, annexText, selectedReportType);
      }
    };

    const removeListener = window.electronAPI.on('recorder-window-hide', handleRecorderWindowHide);

    return () => {
      if (removeListener) removeListener();
    };
  }, [transcript, annexText, selectedReportType, saveOrUpdateDraft]);

  useEffect(() => {
    const handleKeyboardShortcut = async (e) => {
      // Save draft on Ctrl+S / Cmd+S
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        const hasContent = transcript?.trim() || annexText?.trim();
        if (hasContent) {

          await saveOrUpdateDraft(transcript, annexText, selectedReportType);
          showNotification('Brouillon sauvegardé', 'success');
        }
      }

      // Stop recording and send live on F10
      if (e.key === 'F10' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        await handleStopAndSendLive();
      }
    };

    window.addEventListener('keydown', handleKeyboardShortcut);
    return () => window.removeEventListener('keydown', handleKeyboardShortcut);
  }, [transcript, annexText, selectedReportType, saveOrUpdateDraft, showNotification, handleStopAndSendLive]);
  
  useEffect(() => {
    if (!isCapacitor) return;
  
    const handleAppPause = async () => {
      const hasContent = transcript?.trim() || annexText?.trim();
      if (hasContent) {
        
        await saveOrUpdateDraft(transcript, annexText, selectedReportType);
      }
    };
  
    // Only listen for app pause (screen lock, app going to background)
    // Not listening to appStateChange to avoid frequent saves
    CapacitorApp.addListener('pause', handleAppPause);
  
    return () => {
      CapacitorApp.removeAllListeners();
    };
  }, [transcript, annexText, selectedReportType, saveOrUpdateDraft]);
  
  
  const handleToggleDrafts = useCallback(() => {
    setDraftPanelOpen(prev => {
      const newState = !prev;
      if (newState) {
        // Refresh drafts when opening the panel
        loadDrafts();
      }
      return newState;
    });
  }, [loadDrafts]);

  useEffect(() => {
    loadDrafts();
  }, []); // Only on mount
  

const handleSelectDraft = useCallback(
  async (draft, actionMode = 'replaceAll') => {
    // Use the draft directly since it was just refreshed when opening the panel
    if (actionMode === 'replaceAll') {
      setTranscript(draft.content || '');
      setAnnexText(draft.annex || '');
      setSelectedReportType(draft.report_type === 'Dictée' ? '' : draft.report_type);
      setHasUserChosenReportType(true);
    } else if (actionMode === 'replaceText') {
      setTranscript(prev => prev + (draft.content || ''));
    } else if (actionMode === 'replaceAnnex') {
      setAnnexText(prev => prev + (draft.annex || ''));
    }
    // Reset append history baseline to the new transcript
    try {
      appendHistoryRef.current = [];
      lastTranscriptValueRef.current = (actionMode === 'replaceAll') ? (draft.content || '') : (transcriptRef.current || '');
      setCanUndoAppend(false);
    } catch (_) {}
    
    // Call the draft manager's select handler
    draftHandleSelectDraft(draft, actionMode);
  },
  [draftHandleSelectDraft]
);

  // ================== EFFACER ==================

  const handleSaveAsDraft = async () => {
    await saveOrUpdateDraft(transcript, annexText, selectedReportType);
    await handleClearInterface();
  };
  
  const handleMoveToTrash = async () => {
    await saveOrUpdateDraft(transcript, annexText, selectedReportType, 'deleted');
    await draftHandleMoveToTrash();
    await handleClearInterface(false, true);
  };
  

  // Etat pour contrôler la visibilité de la modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAirplaneDeletionWarning, setShowAirplaneDeletionWarning] = useState(false);

  // Remplacez le window.confirm par :
  const handleDelete = async () => {
    // Check if in airplane mode with pending audio
    const hasPendingAudio = audioLibrary.some(item => item.status === 'pending');

    if (isGlobalAirplaneMode && hasPendingAudio) {
      setShowAirplaneDeletionWarning(true);
      return;
    }

    // Existing logic
    if (!transcript && !annexText) {
      await handleClearInterface(false, true);
      return;
    } else {
      setShowDeleteModal(true);
    }
  };

// Fonction pour nettoyer l'interface sans logique de sauvegarde
const handleClearInterface = async (isLive = false, deleteAudios = false) => {
  setIsClearing(true);
    // Clear temporary state FIRST
    clearTemporaryState();

    if (isRecording) {
      await stopRecordingCompletely();
    }

    resetRecorder();
    clearQueue();
    clearOcrQueue();
    if (deleteAudios) {
      try {
        clearAudioLibrary();
        // Clear audio library for current draft from localStorage
        if (currentDraftId) {
          clearAudioLibraryForDraft(currentDraftId);
          clearOcrQueueForDraft(currentDraftId);
        }
      } catch (_) {}
    }

    setIsRecording(false);
    setisFakeRecording(false);

    setTranscript('');
    setAnnexText('');
    setIsSending(false);
    // Reset append history when clearing UI
    appendHistoryRef.current = [];
    lastTranscriptValueRef.current = '';
    setCanUndoAppend(false);

    if (!isLive) {
      setSelectedReportType('');
    }
    setHasUserChosenReportType(false);
    setHighlightReportType(false);
    setDidAutoDetect(false);
    setIsDeterminingType(false);
    setHasAutoDetectedForCurrentRecord(false);

    setCurrentDraftId(null);

    setTranscriptionLimitExceeded(false);
    setTranscriptionLimitDetails(null);
    setHasShownOcrTutorial(false);
    setIsDragging(false);

    setTimeout(() => {
      clearTemporaryState();
      setIsClearing(false);
    }, 500);
};

  // ================== SAUVEGARDE MODE AVION ==================
  const handleSaveAirplaneDraft = useCallback(async () => {
    log.info('[Recorder] Saving airplane draft...');
    log.info('[Recorder] Current state - isRecording:', isRecording, 'isFakeRecording:', isFakeRecording, 'isNative:', isNative);

    try {
      let audioSaved = false;
      let ocrSaved = false;

      // 1. If recording, stop and save the audio
      if (isRecording || isFakeRecording) {
        log.info('[Recorder] Stopping recording for airplane save...');

        if (isNative) {
          // NATIVE (iOS/Android): Appeler le plugin directement pour garder l'URI du fichier
          // Le plugin sauvegarde déjà le fichier sur le filesystem
          const { CapacitorM4aRecorder } = await import('capacitor-m4a-recorder');
          const result = await CapacitorM4aRecorder.stopRecording();
          // result = { uri: "file://...", duration: 5000 }

          log.info('[Recorder] Native recording stopped. URI:', result.uri, 'Duration:', result.duration);

          // Mettre à jour les états
          setNativeRecordingState({ isRecording: false, isPaused: false, voiceActivity: 0, isSilent: false });
          setisFakeRecording(false);

          if (result.uri) {
            const durationSeconds = Math.round((result.duration || 0) / 1000);

            // Sauvegarder SEULEMENT l'URI + métadonnées (pas de blob!)
            const savedItemId = airplaneDraftService.saveAudioItem({
              fileUri: result.uri,
              durationSeconds,
              reportType: selectedReportType
            });

            console.log('[handleSaveAirplaneDraft] SAVED item with id:', savedItemId, 'URI:', result.uri);
            log.info('[Recorder] Airplane audio saved with URI:', result.uri, 'duration:', durationSeconds);

            // Debug: show all stored items
            const allItems = airplaneDraftService.getAllItems();
            console.log('[handleSaveAirplaneDraft] All airplane items after save:', JSON.stringify(allItems));

            audioSaved = true;
          } else {
            log.warn('[Recorder] No URI returned from native recording');
          }
        } else {
          // WEB: Utiliser stopRecording + IndexedDB (fallback)
          const audioBlob = await stopRecording();
          setisFakeRecording(false);

          if (audioBlob && audioBlob.size > 0) {
            const durationMs = recordingDuration;
            const durationSeconds = durationMs > 0 ? Math.round(durationMs / 1000) : 0;

            const itemId = airplaneDraftService.saveAudioItem({
              fileUri: null, // Pas d'URI sur web
              durationSeconds,
              reportType: selectedReportType
            });

            // Save blob to IndexedDB pour web
            await audioStorageService.saveAirplaneItem(itemId, audioBlob, 'audio');
            log.info('[Recorder] Web audio saved to IndexedDB with id:', itemId, 'size:', audioBlob.size);
            audioSaved = true;
          } else {
            log.warn('[Recorder] No audio blob captured or empty blob');
          }
        }
      }

      // 2. Save any pending OCR files
      let ocrFilesCount = 0;
      if (ocrQueue && ocrQueue.length > 0) {
        ocrFilesCount = ocrQueue.length;
        log.info('[Recorder] Saving', ocrFilesCount, 'pending OCR files...');

        for (const file of ocrQueue) {
          try {
            if (isNative) {
              // NATIVE: Save to filesystem
              const { Filesystem, Directory } = await import('@capacitor/filesystem');

              // Read file as base64
              const base64Data = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                  const result = reader.result;
                  // Remove prefix "data:image/png;base64,"
                  resolve(result.split(',')[1]);
                };
                reader.onerror = reject;
                reader.readAsDataURL(file);
              });

              // Generate unique filename
              const uniqueFileName = `ocr_${Date.now()}_${file.name}`;

              // Write to filesystem
              const result = await Filesystem.writeFile({
                path: uniqueFileName,
                data: base64Data,
                directory: Directory.Documents
              });

              // Save metadata with URI
              airplaneDraftService.saveOcrItem({
                fileUri: result.uri,
                fileName: file.name,
                fileType: file.type,
                fileSize: file.size,
                reportType: selectedReportType
              });

              log.info('[Recorder] OCR file saved to filesystem:', result.uri);
            } else {
              // WEB: Save to IndexedDB
              const itemId = airplaneDraftService.saveOcrItem({
                fileUri: null,
                fileName: file.name,
                fileType: file.type,
                fileSize: file.size,
                reportType: selectedReportType
              });
              await audioStorageService.saveAirplaneItem(itemId, file, 'ocr');
              log.info('[Recorder] OCR file saved to IndexedDB:', itemId);
            }
            ocrSaved = true;
          } catch (fileError) {
            log.error('[Recorder] Error saving OCR file:', file.name, fileError);
          }
        }

        // Clear the OCR queue after saving
        clearOcrQueue();
      }

      // 3. Save any existing transcript/annex as a text draft
      let textSaved = false;
      if (transcript?.trim() || annexText?.trim()) {
        const textItemId = airplaneDraftService.saveTextItem({
          transcript: transcript || '',
          annex: annexText || '',
          reportType: selectedReportType
        });
        if (textItemId) {
          log.info('[Recorder] Text draft saved with id:', textItemId);
          textSaved = true;
        }
      }

      // 4. Clear the interface
      if (audioSaved || ocrSaved || textSaved) {
        handleClearInterface();

        // 4. Reload airplane items
        const items = airplaneDraftService.getAllItems();
        setAirplaneItems(items);

        // Haptic feedback on mobile
        if (isCapacitor) {
          try { await Haptics.impact({ style: ImpactStyle.Medium }); } catch (_) {}
        }

        const savedCount = (audioSaved ? 1 : 0) + (ocrSaved ? ocrFilesCount : 0) + (textSaved ? 1 : 0);
        showNotification(
          `${savedCount} élément(s) sauvegardé(s) en mode avion. Retrouvez-les dans l'Historique.`,
          'info'
        );
      } else {
        showNotification('Rien à sauvegarder. Commencez une dictée ou ajoutez un fichier.', 'info');
      }

    } catch (error) {
      log.error('[Recorder] Error saving airplane draft:', error);
      showNotification('Erreur lors de la sauvegarde. Veuillez réessayer.', 'error');
    }
  }, [
    isRecording,
    isFakeRecording,
    stopRecording,
    setisFakeRecording,
    setNativeRecordingState,
    recordingDuration,
    selectedReportType,
    ocrQueue,
    clearOcrQueue,
    handleClearInterface,
    showNotification,
    isCapacitor,
    isNative,
    transcript,
    annexText
  ]);

  // ================== TRAITEMENT ITEM MODE AVION ==================
  const handleAirplaneItemTap = useCallback(async (item) => {
    console.log('[handleAirplaneItemTap] CALLED with item:', JSON.stringify(item));
    log.info('[Recorder] Processing airplane item:', item.id, item.type, 'fileUri:', item.fileUri);

    // Check if online (not required for text items)
    console.log('[handleAirplaneItemTap] isOnline:', isOnline, 'item.type:', item.type);
    if (!isOnline && item.type !== 'text') {
      showNotification('Connexion internet requise pour transcrire.', 'error');
      return;
    }

    // Close drafts panel
    console.log('[handleAirplaneItemTap] Closing panel and starting processing...');
    setDraftPanelOpen(false);

    setIsProcessingAirplaneItem(true);

    try {
      let audioBlob = null;
      let fileUri = item.fileUri;

      console.log('[handleAirplaneItemTap] item.type:', item.type, 'isNative:', isNative, 'fileUri:', fileUri);

      if (item.type === 'audio') {
        // Transcribe audio
        console.log('[handleAirplaneItemTap] Showing notification...');
        showNotification('Transcription en cours...', 'info');

        if (isNative && fileUri) {
          // NATIVE: Lire le fichier via convertFileSrc + fetch (pattern éprouvé)
          console.log('[handleAirplaneItemTap] NATIVE path - reading file...');
          log.info('[Recorder] Reading audio file from filesystem:', fileUri);

          try {
            console.log('[handleAirplaneItemTap] Reading file with Filesystem API...');
            const { Filesystem, Directory } = await import('@capacitor/filesystem');

            // Extract just the filename from the full path
            // /var/mobile/.../Documents/recording_xxx.m4a -> recording_xxx.m4a
            const fileName = fileUri.split('/').pop();
            console.log('[handleAirplaneItemTap] fileName:', fileName);

            // Read the file as base64
            const fileData = await Filesystem.readFile({
              path: fileName,
              directory: Directory.Documents
            });
            console.log('[handleAirplaneItemTap] File read, data length:', fileData.data?.length || 0);

            // Convert base64 to Blob
            const binaryString = atob(fileData.data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            audioBlob = new Blob([bytes], { type: 'audio/m4a' });
            console.log('[handleAirplaneItemTap] audioBlob size:', audioBlob.size);

            log.info('[Recorder] Audio file read via Filesystem API, size:', audioBlob.size);
          } catch (readError) {
            console.error('[handleAirplaneItemTap] READ ERROR:', readError, JSON.stringify(readError));
            log.error('[Recorder] Error reading file from filesystem:', readError);
            showNotification('Fichier audio introuvable. Il a peut-être été supprimé.', 'error');
            airplaneDraftService.deleteItem(item.id);
            setAirplaneItems(airplaneDraftService.getAllItems());
            return;
          }
        } else {
          // WEB: Fallback - Lire depuis IndexedDB
          const storedItem = await audioStorageService.getAirplaneItem(item.id);

          if (!storedItem || !storedItem.blob) {
            showNotification('Fichier introuvable. Il a peut-être expiré.', 'error');
            airplaneDraftService.deleteItem(item.id);
            setAirplaneItems(airplaneDraftService.getAllItems());
            return;
          }

          audioBlob = storedItem.blob;
          log.info('[Recorder] Audio read from IndexedDB, size:', audioBlob.size);
        }

        // Créer le fichier pour la transcription
        const fileType = 'audio/mp4'; // Use standard MIME type
        const fileName = 'airplane_audio.m4a';
        console.log('[handleAirplaneItemTap] Creating File object with blob size:', audioBlob.size, 'type:', fileType);
        const audioFile = new File([audioBlob], fileName, { type: fileType });
        console.log('[handleAirplaneItemTap] File created - name:', audioFile.name, 'size:', audioFile.size, 'type:', audioFile.type);

        console.log('[handleAirplaneItemTap] Calling transcribeAudio...');
        const result = await reportService.transcribeAudio(audioFile, {
          reportType: item.reportType || selectedReportType || null,
          durationSeconds: item.durationSeconds || 0
        });
        console.log('[handleAirplaneItemTap] Transcription result:', JSON.stringify(result), 'type:', typeof result);

        // transcribeAudio returns the transcription string directly, not wrapped in an object
        const transcribedText = typeof result === 'string' ? result : (result?.data?.transcription || result?.transcription || '');
        console.log('[handleAirplaneItemTap] Transcribed text:', transcribedText ? transcribedText.substring(0, 50) + '...' : '(empty)');

        if (transcribedText) {
          // Append to transcript
          setTranscript(prev => {
            const separator = prev && prev.trim() ? '\n' : '';
            return prev + separator + transcribedText;
          });
          showNotification('Audio transcrit avec succès.', 'success');

          // Supprimer le fichier du filesystem après succès (natif uniquement)
          if (isNative && fileUri) {
            try {
              const { Filesystem, Directory } = await import('@capacitor/filesystem');
              // Extraire le nom du fichier de l'URI (file:///Documents/recording_xxx.m4a -> recording_xxx.m4a)
              const fileName = fileUri.split('/').pop();
              await Filesystem.deleteFile({
                path: fileName,
                directory: Directory.Documents
              });
              log.info('[Recorder] Audio file deleted from filesystem:', fileName);
            } catch (deleteError) {
              log.warn('[Recorder] Could not delete audio file:', deleteError);
            }
          } else {
            // Web: supprimer de IndexedDB
            await audioStorageService.deleteAirplaneItem(item.id);
          }
        } else {
          showNotification('Aucun texte détecté dans l\'audio.', 'warning');
        }

      } else if (item.type === 'ocr') {
        showNotification('Extraction de texte en cours...', 'info');

        let fileBlob = null;

        if (isNative && item.fileUri) {
          // NATIVE: Read from filesystem
          try {
            const { Filesystem, Directory } = await import('@capacitor/filesystem');
            // Remove trailing slash before extracting filename
            const fileName = item.fileUri.replace(/\/$/, '').split('/').pop();
            console.log('[handleAirplaneItemTap] OCR - Reading file:', fileName);

            const fileData = await Filesystem.readFile({
              path: fileName,
              directory: Directory.Documents
            });

            // Convert base64 to Blob
            console.log('[handleAirplaneItemTap] OCR - Converting base64 to blob, data length:', fileData.data?.length);
            const binaryString = atob(fileData.data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            fileBlob = new Blob([bytes], { type: item.fileType || 'image/png' });
            console.log('[handleAirplaneItemTap] OCR - Blob created, size:', fileBlob.size);
          } catch (readError) {
            console.error('[handleAirplaneItemTap] OCR - Read error:', readError, JSON.stringify(readError));
            showNotification('Fichier introuvable. Il a peut-être été supprimé.', 'error');
            airplaneDraftService.deleteItem(item.id);
            setAirplaneItems(airplaneDraftService.getAllItems());
            return;
          }
        } else {
          // WEB: Read from IndexedDB
          const storedItem = await audioStorageService.getAirplaneItem(item.id);

          if (!storedItem || !storedItem.blob) {
            showNotification('Fichier introuvable. Il a peut-être expiré.', 'error');
            airplaneDraftService.deleteItem(item.id);
            setAirplaneItems(airplaneDraftService.getAllItems());
            return;
          }
          fileBlob = storedItem.blob;
        }

        // Create File and perform OCR
        console.log('[handleAirplaneItemTap] OCR - Creating File object from blob:', fileBlob?.size);
        const file = new File([fileBlob], item.fileName || 'file', { type: item.fileType || 'image/png' });
        console.log('[handleAirplaneItemTap] OCR - File created:', file.name, file.size, file.type);

        // Use ocrService directly
        console.log('[handleAirplaneItemTap] OCR - Calling ocrService.extractText...');
        const { ocrService } = await import('../services');
        const result = await ocrService.extractText(file, item.reportType || selectedReportType);
        console.log('[handleAirplaneItemTap] OCR - Result:', result);

        const extractedText = result?.content || result?.text || '';

        if (extractedText) {
          // Append to annex
          setAnnexText(prev => {
            const prefix = prev && prev.trim() ? '\n\nProchain annexe:\n' : 'Prochain annexe:\n';
            return prev + prefix + extractedText + '\n';
          });
          showNotification('Texte extrait avec succès.', 'success');
        } else {
          showNotification('Aucun texte détecté dans le fichier.', 'warning');
        }

        // Delete file after successful processing
        if (isNative && item.fileUri) {
          try {
            const { Filesystem, Directory } = await import('@capacitor/filesystem');
            // Remove trailing slash before extracting filename
            const deleteFileName = item.fileUri.replace(/\/$/, '').split('/').pop();
            await Filesystem.deleteFile({
              path: deleteFileName,
              directory: Directory.Documents
            });
            log.info('[Recorder] OCR file deleted from filesystem:', deleteFileName);
          } catch (deleteError) {
            log.warn('[Recorder] Could not delete OCR file:', deleteError);
          }
        } else {
          await audioStorageService.deleteAirplaneItem(item.id);
        }
      } else if (item.type === 'text') {
        // Load text draft into dictaphone
        console.log('[handleAirplaneItemTap] Loading text draft...');

        // Load transcript if available
        if (item.transcript?.trim()) {
          setTranscript(prev => {
            const separator = prev && prev.trim() ? '\n' : '';
            return prev + separator + item.transcript;
          });
        }

        // Load annex if available
        if (item.annex?.trim()) {
          setAnnexText(prev => {
            const separator = prev && prev.trim() ? '\n' : '';
            return prev + separator + item.annex;
          });
        }

        // Set report type if available
        if (item.reportType) {
          setSelectedReportType(item.reportType);
        }

        showNotification('Brouillon chargé avec succès.', 'success');
      }

      // Supprimer les métadonnées de l'item
      airplaneDraftService.deleteItem(item.id);
      setAirplaneItems(airplaneDraftService.getAllItems());

      // Haptic feedback
      if (isCapacitor) {
        try { await Haptics.impact({ style: ImpactStyle.Light }); } catch (_) {}
      }

    } catch (error) {
      console.error('[handleAirplaneItemTap] OUTER CATCH ERROR:', error?.message || error, error?.stack);
      log.error('[Recorder] Error processing airplane item:', error?.message || error);
      showNotification('Erreur lors du traitement. L\'élément reste disponible.', 'error');
    } finally {
      setIsProcessingAirplaneItem(false);
    }
  }, [
    isOnline,
    isNative,
    showNotification,
    selectedReportType,
    setTranscript,
    setAnnexText,
    setSelectedReportType,
    isCapacitor
  ]);

  // ================== VIDER ITEMS MODE AVION ==================
  const handleClearAirplaneItems = useCallback(async () => {
    try {
      await audioStorageService.deleteAllAirplaneItems();
      airplaneDraftService.deleteAllItems();
      setAirplaneItems([]);
      showNotification('Tous les éléments mode avion ont été supprimés.', 'info');
    } catch (error) {
      log.error('[Recorder] Error clearing airplane items:', error);
      showNotification('Erreur lors de la suppression.', 'error');
    }
  }, [showNotification]);

  function formatDate(ts) {
    return new Date(ts).toLocaleString('fr-FR', {
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  // Calculate time until reset
  const getTimeUntilReset = useCallback(() => {
    if (!resetAt) return '';
    
    const now = new Date();
    const resetTime = new Date(resetAt);
    const diff = resetTime - now;
    
    if (diff <= 0) return 'Actualisation imminente...';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}min`;
    }
    return `${minutes} minutes`;
  }, [resetAt]);

  // Update timer every minute and check if limit has reset
  const [, forceUpdate] = useState({});
  useEffect(() => {
    if (isUsageLimitReached && resetAt) {
      const checkResetTime = () => {
        const resetTime = new Date(resetAt);
        const now = new Date();

        if (now >= resetTime) {

          setIsUsageLimitReached(false);
          BaseService.setUsageLimit(false);
          // Refresh usage data with forced check
          checkUsageBeforeRecord();
        } else {
          forceUpdate({});
        }
      };

      // Check immediately
      checkResetTime();

      const interval = setInterval(checkResetTime, 60000); // Update every minute

      return () => clearInterval(interval);
    }
  }, [isUsageLimitReached, resetAt, checkUsageBeforeRecord]);

  // Dans liveRecorder.js, modifier le renderRecordBtnContent :

const renderRecordBtnContent = () => {
  const isSmall = isElectron && isLiveMode;
  const iconSize = isSmall ? 28 : (isElectron ? 40 : 60);
  // While send button is loading, show microphone (not play)
  if (isSending) {
    return <FaMicrophone size={iconSize} />;
  }
  // Show loading only for unformatted live send while waiting for transcription
  if (isElectron && isLiveMode && isLiveProcessing && !liveFormatOnStopRef.current) {
    return <FaSpinner className="fa-spin" size={iconSize} />;
  }
  /* MICROPHONE: seulement si RIEN ne se passe */
  if (!isFakeRecording) {
    return <FaMicrophone size={iconSize} />;
  }

  /* WAITING STATE: Ready for next recording - show play icon */
  if (isFakeRecording && !isRecording) {
    return <FaPlay size={iconSize} />;
  }

    /* EN ENREGISTREMENT - Visualiseur avec détection de voix */
    return (
      <div className="record-inner">
        <MicVisualizer
          active
          voiceActivity={voiceActivity}
          isSilent={isSilent}
          width={isSmall ? 60 : 80}
          height={isSmall ? 42 : (isElectron ? 55 : 90)}
          color="#ffffff"
          barWidth={isSmall ? 3 : (isElectron ? 4 : 8)}
          numBars={isSmall ? 14 : 16}
          mirror={true}
          className="mic-canvas"
        />
      </div>
    );

};



  const containerClass = isElectron && !isLiveMode
    ? 'mobile-dictaphone-container electron-dictaphone-container'
    : 'mobile-dictaphone-container';

  // Handle drag and drop for audio files (Electron and Web)
  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    // Filter for MP3 files only
    const mp3Files = Array.from(files).filter(file =>
      file.type === 'audio/mpeg' ||
      file.type === 'audio/mp3' ||
      /\.mp3$/i.test(file.name)
    );

    if (mp3Files.length === 0) {
      showNotification('Veuillez déposer des fichiers MP3 uniquement', 'warning');
      return;
    }

    // On Electron, use existing file handling
    if (isElectron) {
      onFileChange({ target: { files } });
      return;
    }

    // On Web, transcribe MP3 files and append to text area
    showNotification(`Transcription de ${mp3Files.length} fichier(s) MP3 en cours...`, 'info');
    setIsTranscribingMp3(true);

    try {
      for (const mp3File of mp3Files) {
        try {
          log.info('Transcribing MP3 file:', mp3File.name);
          const transcriptionText = await reportService.transcribeAudio(mp3File, {
            reportType: selectedReportType || undefined
          });

          // Append transcription to text area (same pattern as recording flow)
          setTranscript(prev => prev + transcriptionText + ' ');

          showNotification(`${mp3File.name} transcrit avec succès`, 'success');
        } catch (error) {
          log.error('Error transcribing MP3 file:', error);
          showNotification(`Erreur lors de la transcription de ${mp3File.name}`, 'error');
        }
      }
    } finally {
      setIsTranscribingMp3(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set isDragging to false if we're leaving the container
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  // Toggle global airplane mode and sync queue state
  const toggleGlobalAirplaneMode = useCallback(async () => {
    const newValue = !isGlobalAirplaneMode;
    setIsGlobalAirplaneMode(newValue);
    try { localStorage.setItem(AIRPLANE_MODE_KEY, newValue ? '1' : '0'); } catch (_) {}
    if (newValue) {
      await handlePauseAll();
    } else {
      await handleResumeAll();
      // Resume OCR queue processing if there are pending files
      if (ocrQueueLength > 0 && isOnline) {
        processOcrQueue();
      }
    }
  }, [isGlobalAirplaneMode, handlePauseAll, handleResumeAll, showNotification, ocrQueueLength, isOnline, processOcrQueue]);

  
  return (
    <div
      className={containerClass}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
    >
      {/* Live insert overlay (Electron Live mode only) */}
      {isElectron && isLiveMode && liveOverlayText && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10050,
            backdropFilter: 'blur(2px)',
            transition: 'opacity 280ms ease',
            opacity: liveOverlayVisible ? 1 : 0
          }}
        >
          <div
            style={{
              background: 'linear-gradient(180deg, #ffffff 0%, #f9fbff 100%)',
              borderRadius: 18,
              padding: 20,
              width: 'min(680px, 90vw)',
              boxShadow: '0 14px 48px rgba(0,0,0,0.18)',
              border: '1px solid rgba(0,0,0,0.06)',
              transform: `translateY(${liveOverlayVisible ? 0 : 8}px)`,
              transition: 'transform 280ms ease'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <FaCheckCircle size={22} style={{ color: 'var(--color-success)' }} />
              <div style={{ fontWeight: 700, color: '#222', fontSize: 16 }}>Texte inséré</div>
            </div>
            <div
              style={{
                backgroundColor: '#ffffff',
                border: '1px solid #e6ecf3',
                borderRadius: 12,
                padding: '12px 16px',
                color: '#2b2b2b',
                lineHeight: 1.5,
                fontSize: 14,
                height: '40px',
                maxWidth: '600px',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              {liveOverlayText}
            </div>
            <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#667085', fontSize: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <FaClipboard size={12} />
                <span>Copié dans le presse-papiers</span>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Format prompt overlay (Electron Live mode only) */}
      {isElectron && isLiveMode && formatPromptActive && (
        <div
          style={{
            position: 'fixed',
            left: '50%',
            bottom: 16,
            transform: 'translateX(-50%)',
            background: 'white',
            borderRadius: 12,
            padding: '12px 16px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
            border: '1px solid rgba(0,0,0,0.06)',
            width: 'min(420px, 92vw)',
            zIndex: 10060
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ color: '#333', fontWeight: 600, fontSize: 14 }}>
              Appuyez encore pour mettre en forme
            </div>
          </div>
          <div style={{
            position: 'relative',
            width: '100%',
            height: 6,
            borderRadius: 999,
            background: '#eef2f7',
            overflow: 'hidden',
            border: '1px solid rgba(0,0,0,0.05)'
          }}>
            <div style={{
              width: `${formatPromptProgress}%`,
              height: '100%',
              background: 'linear-gradient(90deg, var(--color-primary), #ff6b6b)',
              transition: 'width 33ms linear'
            }} />
          </div>
        </div>
      )}
      {/* Drag and drop overlay for Electron */}
      {isElectron && isDragging && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(33, 150, 243, 0.1)',
            border: '3px dashed #2196f3',
            borderRadius: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9998,
            pointerEvents: 'none',
            margin: '10px'
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '20px',
              padding: '40px',
              textAlign: 'center',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)'
            }}
          >
            <FaCamera size={60} style={{ color: '#2196f3', marginBottom: '20px' }} />
            <h2 style={{ color: '#333', marginBottom: '10px' }}>
              Déposez vos fichiers ici
            </h2>
            <p style={{ color: '#666' }}>
              Images, PDF, documents Word ou texte
            </p>
          </div>
        </div>
      )}

       {notification && (
        <Notification
          key={notification.id}
          id={notification.id}
          message={notification.message}
          type={notification.type}
          onClose={hideNotification}
        />
      )}

      {/* Tutorial Modal */}
      {currentTutorial && (
        <TutorialModal
          isOpen={showTutorial}
          onClose={handleTutorialClose}
          tutorialId={currentTutorial.id}
          title={currentTutorial.title}
          message={currentTutorial.message}
          onDontShowAgain={handleTutorialDontShowAgain}
          currentStep={currentTutorial.currentStep}
          totalSteps={currentTutorial.totalSteps}
        />
      )}

      {/* Audio Queue Error Dialog */}
      {errorDialogState && (
        <ErrorDialog
          isOpen={true}
          onClose={() => setErrorDialogState(null)}
          onRetry={() => handleErrorRetry(errorDialogState.audioId)}
          onDelete={() => handleErrorDelete(errorDialogState.audioId)}
          onAirplaneMode={() => {
            // Enable airplane mode
            toggleGlobalAirplaneMode();
            // Delete the failed audio
            handleErrorDelete(errorDialogState.audioId);
          }}
          errorMessage={errorDialogState.errorMessage}
          itemDescription={errorDialogState.itemDescription}
        />
      )}

      {/* OCR Error Dialog */}
      {ocrErrorDialogState && (
        <ErrorDialog
          isOpen={true}
          onClose={() => setOcrErrorDialogState(null)}
          onRetry={() => ocrErrorDialogState.isBatch ? 
            handleOcrErrorRetry(ocrErrorDialogState.failedFiles) : 
            handleOcrErrorRetry(ocrErrorDialogState.file)}
          onDelete={() => ocrErrorDialogState.isBatch ? 
            handleOcrErrorDelete(ocrErrorDialogState.failedFiles) : 
            handleOcrErrorDelete(ocrErrorDialogState.file)}
          errorMessage={ocrErrorDialogState.errorMessage}
          itemDescription={ocrErrorDialogState.itemDescription}
          isBatch={ocrErrorDialogState.isBatch}
          failedFiles={ocrErrorDialogState.failedFiles}
          errorGroups={ocrErrorDialogState.errorGroups}
        />
      )}

      {/* Conditional rendering: report type panel, drafts panel, live mode (Electron), or main view */}
      {showReportTypePanel ? (

          <div className="dictaphone-fullscreen-view">
            <div
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px', borderBottom: '1px solid #e0e0e0' }}
            >
              <h4 style={{ margin: 0 }}>Modèle de rapport</h4>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button icon={FaPencilAlt} variant="secondary" onClick={async () => {
                  try { setShowReportTypePanel(false); } catch (_) {}
                  try {
                    if (isElectron && window?.electronAPI?.invoke) {
                      await window.electronAPI.invoke('recorder:hide');
                      await window.electronAPI.invoke('app:navigate', '/report-types/');
                      await window.electronAPI.invoke('app:show');
                      return;
                    }
                  } catch (_) {}
                  navigate('/report-types/', { state: { from: 'recorder' } });
                }}>
                  Modifier
                </Button>
                <Button icon={FaTimes} variant="secondary" onClick={() => setShowReportTypePanel(false)}>
                </Button>
              </div>
            </div>
            <div style={{ padding: '20px', height: 'calc(100% - 70px)', overflow: 'auto' }}>
              <ReportTypeSelector
                reportTypes={reportTypes}
                onSelect={handleSelectReportType}
                onReportTypesChange={setReportTypes}
                liveMode={false}
              />
            </div>
          </div>
        
      ) : draftPanelOpen ? (
        <div className="dictaphone-fullscreen-view">
          {((activeDrafts.length + sentDrafts.length + deletedDrafts.length + liveDrafts.length) === 0 && isLoadingDrafts) ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <FaSpinner className="fa-spin" size={24} />
              <p>Chargement des brouillons...</p>
            </div>
          ) : ((activeDrafts.length + sentDrafts.length + deletedDrafts.length + liveDrafts.length) === 0 && draftError) ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <p style={{ color: 'red' }}>Erreur: {draftError}</p>
              <Button onClick={loadDrafts}>Réessayer</Button>
            </div>
          ) : (
            <DraftsPanel
              activeDrafts={activeDrafts}
              sentDrafts={sentDrafts}
              deletedDrafts={deletedDrafts}
              liveDrafts={liveDrafts}
              airplaneItems={airplaneItems}
              onSelectDraft={handleSelectDraft}
              onDeleteDraft={(id, permanent = false) => handleDeleteDraft(id, permanent)}
              onDeleteAllDraft={(ids, permanent = false) => handleDeleteAllDrafts(ids, permanent)}
              onBulkClearSection={(section) => handleBulkClearSection(section)}
              onAirplaneItemTap={handleAirplaneItemTap}
              onClearAirplaneItems={handleClearAirplaneItems}
              onRestoreDeleted={handleRestoreDeleted}
              onClose={() => setDraftPanelOpen(false)}
              formatDate={formatDate}
            />
          )}
        </div>
      ) : (isElectron && isLiveMode) ? (
        <LiveRecorder
          triggerRecord={triggerRecord}
          renderRecordBtnContent={renderRecordBtnContent}
          isUsageLimitReached={isUsageLimitReached}
          toggleRecordShortcut={toggleRecordShortcut}
          handleSendLive={handleSendLive}
          isSending={isSending}
          isRecording={isRecording}
          isFakeRecording={isFakeRecording}
          isLiveProcessing={isLiveProcessing}
          liveFormatOnStopRef={liveFormatOnStopRef}
        />
      ) : (
        <>
          {/* Main dictaphone view */}
          {/* Electron-only title bar */}
          {isElectron && (
            <div className="electron-dictaphone-header">
              <div className="electron-dictaphone-header-left">
                <Button
                  icon={FaTimes}
                  variant="primary"
                  onClick={async () => {
                    try { await window?.electronAPI?.invoke?.('recorder:toggle'); } catch (_) {}
                  }}
                  collapseOnSmallScreen={false}
                  style={{ WebkitAppRegion: 'no-drag' }}
                >
                </Button>
              </div>
              <div className="electron-dictaphone-header-center">
                <div className="electron-dictaphone-indicator">
                  <FaMicrophone className="electron-dictaphone-icon" />
                  <span className="electron-dictaphone-text">DICTAPHONE</span>
                </div>
              </div>
              <div className="electron-dictaphone-header-right">
                {/* Live toggle removed; controlled from Header */}
              </div>
            </div>
          )}
          {/* Top bar : Template, brouillons, etc. */}
          <div className="dictaphone-top-bar" style={{ display: 'flex', alignItems: 'center' }}>
        <div className="dictaphone-top-bar-left" style={{ display: 'flex', alignItems: 'center' }}>
          <Button
            icon={isDeterminingType && !highlightReportType ? FaSpinner : highlightReportType && !selectedReportType ? FaQuestionCircle : FaClipboardList}
            isSpinning={isDeterminingType && !highlightReportType}
            variant={
              highlightReportType && !selectedReportType
                ? 'orange'
                : selectedReportType
                  ? `button ${getButtonClass(selectedReportType)}`
                  : 'blue'
            }
            highlight={highlightReportType && !selectedReportType}
            onClick={handleToggleReportTypePanel}
            collapseOnSmallScreen={false}
            disabled={isUsageLimitReached}
            style={{
              opacity: isUsageLimitReached ? 0.5 : 1,
              cursor: isUsageLimitReached ? 'not-allowed' : 'pointer',
              padding: '10px 18px',
              fontSize: selectedReportType && selectedReportType.length > 12 ? 14 : 16,
              fontWeight: 'bold',
              borderRadius: 24,
              boxShadow: highlightReportType && !selectedReportType
                ? '0 0 0 3px rgba(255, 167, 38, 0.45), 0 6px 14px rgba(0,0,0,0.12)'
                : '0 6px 14px rgba(0,0,0,0.12)',
              border: highlightReportType && !selectedReportType ? '2px solid #ffa726' : '2px solid transparent',
              width: 160,
              minWidth: 160,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {selectedReportType ? (
              formatReportTypeDisplayName(selectedReportType)
            ) : (
              'Modèle'
            )}
          </Button>
        </div>

      
        {/* Bouton statut de la queue moved to transcript overlay */}

        {/* Indicateur d'usage */}
        <UsageCircleView
          isGold={isGold}
          usageLeft={usageLeft}
          usageTotal={usageTotal}
          resetAt={resetAt}
        />

        {/* Brouillons */}
        <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
          <Button
            icon={FaFile}
            variant={activeDrafts.length > 0 || airplaneItems.length > 0 ? 'info' : 'secondary'}
            onClick={handleToggleDrafts}
            collapseOnSmallScreen={false}
          >
            {draftPanelOpen ? 'Fermer' : 'Historique'}
          </Button>
          {/* Badge for airplane items */}
          {airplaneItems.length > 0 && !draftPanelOpen && (
            <span
              style={{
                position: 'absolute',
                top: '-6px',
                right: '-6px',
                background: '#f97316',
                color: 'white',
                borderRadius: '50%',
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '11px',
                fontWeight: 'bold',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}
            >
              {airplaneItems.length}
            </span>
          )}
        </div>

        {/* Mode Live toggle moved to Electron title bar */}

      </div>

      {/* Zone texte dicté */}
      <Card className="dictaphone-transcript-card">
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
          {/* Queue status button – overlay top-right of transcript */}
          <div
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              zIndex: 3
            }}
          >
            <button
              className="queue-status-button"
              onClick={() => setShowQueuePanel(true)}
              title="File d'attente de dictée"
            >
              {/* Icône selon l'état */}
              {(isTranscribing || isTranscribingMp3 || isProcessingAirplaneItem) ? (
                <FaSpinner size={16} className="queue-status-transcribing" />
              ) : isFakeRecording ? (
                !isOnline ? (
                  <MdSignalWifiOff size={16} style={{ color: 'red' }}/>
                ) : (
                  <FaMicrophone size={16} className="queue-status-recording" />
                )
              ) : (
                !isOnline ? (
                  <MdSignalWifiOff size={16} style={{ color: 'red' }}/>
                ) : (
                  <FaMicrophone size={16} className="queue-status-idle" />
                )
              )}
              {/* Badge avec le nombre d'éléments en queue */}
              {queueWithMetadata.length > 0 && (
                <span className="queue-badge">{queueWithMetadata.length}</span>
              )}
            </button>
          </div>
          <textarea
  ref={textareaRef}
  className="dictaphone-textarea"
  {...(isCapacitor
    ? transcriptTextareaProps
    : {
        value: transcript,
        onChange: (e) => setTranscript(e.target.value)
      }
  )}
  placeholder=""
/>

          {/* Airplane mode toggle - bottom left overlay (Capacitor only) */}
          {isCapacitor && (
            <button
              onClick={toggleGlobalAirplaneMode}
              style={{
                position: 'absolute',
                left: '10px',
                bottom: '10px',
                width: '36px',
                height: '36px',
                borderRadius: '18px',
                border: 'none',
                backgroundColor: isGlobalAirplaneMode ? '#ff9800' : 'rgba(0,0,0,0.6)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 2,
                transition: 'background-color 0.3s ease'
              }}
              title={isGlobalAirplaneMode ? "Désactiver le mode avion" : "Activer le mode avion"}
            >
              <FaPlane size={16} />
            </button>
          )}

          {/* Undo last append button - bottom right overlay */}
          {canUndoAppend && (
            <button
              onClick={handleUndoAppend}
              style={{
                position: 'absolute',
                right: '10px',
                bottom: '10px',
                width: '36px',
                height: '36px',
                borderRadius: '18px',
                border: 'none',
                backgroundColor: 'rgba(0,0,0,0.6)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 2
              }}
              title="Annuler le dernier ajout"
            >
              <FaUndo size={16} />
            </button>
          )}
          
          {/* Guide overlay - visible only when textarea is empty */}
          {!transcript && !isFakeRecording && !isTranscribing && !isTranscribingMp3 && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'none',
              width: '85%',
              maxWidth: '320px',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              opacity: 0.6
            }}>
              
              {/* Dicter/Transcrire */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 10px',
                backgroundColor: '#f8f8f8',
                borderRadius: '20px'
              }}>
                <button style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--color-primary)',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '11px',
                  flexShrink: 0,
                  pointerEvents: 'none'
                }}>
                  <FaMicrophone />
                </button>
                <span style={{ fontSize: '12px', color: '#666' }}>
                  Dicter / Arrêter et transcrire
                </span>
              </div>
              
              {/* Scanner un document */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 10px',
                backgroundColor: '#f8f8f8',
                borderRadius: '20px'
              }}>
                <button style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--color-reporttype-consultation)',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '11px',
                  flexShrink: 0,
                  pointerEvents: 'none'
                }}>
                  <FaCamera />
                </button>
                <span style={{ fontSize: '12px', color: '#666' }}>
                  Scanner un document
                </span>
              </div>
              
              {/* Arrêter la dictée */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 10px',
                backgroundColor: '#f8f8f8',
                borderRadius: '20px'
              }}>
                <button style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--color-danger)',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '11px',
                  flexShrink: 0,
                  pointerEvents: 'none'
                }}>
                  <FaTrash />
                </button>
                <span style={{ fontSize: '12px', color: '#666' }}>
                  Arrêter la dictée
                </span>
              </div>
              
              {/* Terminer et générer */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 10px',
                backgroundColor: '#f8f8f8',
                borderRadius: '20px'
              }}>
                <button style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--color-success)',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '11px',
                  flexShrink: 0,
                  pointerEvents: 'none'
                }}>
                  <FaPaperPlane />
                </button>
                <span style={{ fontSize: '12px', color: '#666' }}>
                  Terminer et générer le rapport
                </span>
              </div>
            </div>
          )}
        </div>
      </Card>

      <div className="dictaphone-bottom-controls">

        {/* Boutons du bas : Supprimer, Annexe, Envoyer/Transcrire */}
        <div className="dictaphone-first-row">
          <ButtonRecord
            icon={isExtractingText ? FaSpinner : FaCamera}
            isSpinning={isExtractingText}
            variant={
              annexText.trim() ? 'reporttype-mail' : 'reporttype-consultation'
            }
            onClick={handleToggleAnnex}
            disabled={isUsageLimitReached}
            style={{ 
              position: 'relative',
              opacity: isUsageLimitReached ? 0.5 : 1,
              cursor: isUsageLimitReached ? 'not-allowed' : 'pointer'
            }}
          >
            {/* Show OCR queue count when extracting */}
            {isExtractingText && ocrQueueLength > 0 && (
              <span 
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: 'white',
                  textShadow: '0 0 4px rgba(0,0,0,0.5)',
                  pointerEvents: 'none',
                  zIndex: 10
                }}
              >
                {ocrQueueLength}
              </span>
            )}
          </ButtonRecord>
        </div>
        <div className="dictaphone-second-row">
        
          <ButtonRecord
            icon={FaTrash}
            variant={currentDraftId ? "danger" : "secondary"}
            onClick={handleDelete}
          >
          </ButtonRecord>
        

          <ButtonRecord
            icon={isSending ? FaSpinner : (isGlobalAirplaneMode ? FaSave : FaPaperPlane)}
            isSpinning={isSending}
            variant={isGlobalAirplaneMode ? "info" : "success"}
            onClick={isGlobalAirplaneMode ? handleSaveAirplaneDraft : handleSend}
            disabled={isGlobalAirplaneMode
              ? ((!isRecording && !isFakeRecording && ocrQueueLength === 0) || isSending)
              : ((!transcript.trim() && !annexText.trim()) || isUsageLimitReached || isSending)
            }
            style={{
              opacity: (isUsageLimitReached || isSending) ? 0.5 : 1,
              cursor: (isUsageLimitReached || isSending) ? 'not-allowed' : 'pointer'
            }}
            title={isGlobalAirplaneMode ? "Sauvegarder en brouillon (mode avion)" : "Envoyer le rapport"}
          >

          </ButtonRecord>
        
        </div>
     
      </div>

      {/* Bouton dictée (micro) */}
      <div className="dictaphone-center-row">
          
          <button
    className={isElectron ? 'electron-record-button' : 'record-button'}
    onClick={triggerRecord}
    disabled={isUsageLimitReached}
    style={{
      opacity: isUsageLimitReached ? 0.5 : 1,
      cursor: isUsageLimitReached ? 'not-allowed' : 'pointer'
    }}
  >
    {renderRecordBtnContent()}
  </button>
        </div>
      

        </>
      )}

{showDeleteModal && (
  <div className="custom-modal-overlay">
    <div className="custom-modal">
      <Button
        icon={FaTimes}
        variant="secondary"
        onClick={() => setShowDeleteModal(false)}
        collapseOnSmallScreen={false}
      />
      <p>Souhaitez vous enregistrer cette dictée comme brouillon ?</p>
      <div className="custom-modal-buttons">
        <Button
          icon={FaFile}
          variant="info"
          onClick={async () => {
            await handleSaveAsDraft();
            setShowDeleteModal(false);
          }}
          collapseOnSmallScreen={false}
        >
          Sauvegarder
        </Button>
        <Button
          icon={FaTrash}
          variant="danger"
          onClick={async () => {
            await handleMoveToTrash();
            setShowDeleteModal(false);
          }}
          collapseOnSmallScreen={false}
        >
          Supprimer
        </Button>
      </div>
    </div>
  </div>
)}

{isCapacitor && showAirplaneDeletionWarning && (
  <div className="custom-modal-overlay">
    <div className="custom-modal">
      <Button
        icon={FaTimes}
        variant="secondary"
        onClick={() => setShowAirplaneDeletionWarning(false)}
        collapseOnSmallScreen={false}
      />
      <p style={{ fontWeight: 'bold', color: 'var(--color-warning)' }}>
        ⚠️ Attention mode avion : dictée non transcrite
      </p>
      <p style={{ fontSize: '12px', color: '#666' }}>
        Si vous supprimez, vous allez perdre ce qui n'a pas été transcrit.
      </p>
      <div className="custom-modal-buttons">
        <Button
          icon={FaTrash}
          variant="danger"
          onClick={async () => {
            setShowAirplaneDeletionWarning(false);
            // Continue with normal deletion flow
            if (!transcript && !annexText) {
              await handleClearInterface(false, true);
            } else {
              setShowDeleteModal(true);
            }
          }}
          collapseOnSmallScreen={false}
        >
          Supprimer
        </Button>
        <Button
          icon={FaTimes}
          variant="secondary"
          onClick={() => setShowAirplaneDeletionWarning(false)}
          collapseOnSmallScreen={false}
        >
          Retour
        </Button>
      </div>
    </div>
  </div>
)}

{isCapacitor && showAirplanePrompt && (
  <div className="custom-modal-overlay">
    <div className="custom-modal">
      <Button
        icon={FaTimes}
        variant="secondary"
        onClick={() => {
          setShowAirplanePrompt(false);
          if (airplanePromptResolverRef.current) {
            airplanePromptResolverRef.current(false);
            airplanePromptResolverRef.current = null;
          }
        }}
        collapseOnSmallScreen={false}
      />
      <p>Connexion internet instable. Voulez-vous continuer en mode avion ?</p>
      <p style={{ fontSize: '12px', color: '#666' }}>En mode avion, la dictée est possible mais l\'audio ne sera pas transcrit et les scans ne seront pas traités jusqu\'à désactivation.</p>
      <div className="custom-modal-buttons">
        <Button
          icon={FaPlane}
          variant="warning"
          onClick={() => {
            setShowAirplanePrompt(false);
            if (airplanePromptResolverRef.current) {
              airplanePromptResolverRef.current(true);
              airplanePromptResolverRef.current = null;
            }
          }}
          collapseOnSmallScreen={false}
        >
          Continuer en mode avion
        </Button>
        <Button
          icon={FaTimes}
          variant="secondary"
          onClick={() => {
            setShowAirplanePrompt(false);
            if (airplanePromptResolverRef.current) {
              airplanePromptResolverRef.current(false);
              airplanePromptResolverRef.current = null;
            }
          }}
          collapseOnSmallScreen={false}
        >
          Annuler
        </Button>
      </div>
    </div>
  </div>
)}


      {/* Send Success Animation */}
      <SendSuccessAnimation 
        show={showSendSuccess}
        onComplete={() => {
          setShowSendSuccess(false);
          // Close recorder window after animation completes (Electron)
          if (isElectron && window?.electronAPI) {
            // Set last route to 'reports' so toggleApp reopens reports by default after send
            try { window.electronAPI.setLastRoute?.('reports'); } catch (_) {}
            // Hide recorder window explicitly without changing last route again
            try { window.electronAPI.hideRecorder?.(); } catch (_) {}
          }
        }}
      />


      {/* Usage Limit Overlay */}
      {isUsageLimitReached && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            backdropFilter: 'blur(4px)'
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '20px',
              padding: '40px',
              textAlign: 'center',
              maxWidth: '80%',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
            }}
          >
            {/* Lock Icon */}
            <div style={{ fontSize: '80px', marginBottom: '20px' }}>
              🔒
            </div>

            {/* Title */}
            <h2 style={{ marginBottom: '20px', color: '#333' }}>
              Limite atteinte
            </h2>

            {/* Message */}
            <p style={{ fontSize: '18px', color: '#666', marginBottom: '30px' }}>
              Vous avez atteint votre limite quotidienne de rapports ou de transcriptions.
            </p>

            {/* Reset Time */}
            {resetAt && (
              <div style={{
                backgroundColor: '#f0f0f0',
                borderRadius: '10px',
                padding: '20px',
                marginBottom: '20px'
              }}>
                <p style={{ margin: 0, fontWeight: 'bold', color: '#333' }}>
                  Prochaine actualisation dans:
                </p>
                <p style={{ margin: '10px 0 0 0', fontSize: '24px', color: '#2196f3' }}>
                  {getTimeUntilReset()}
                </p>
              </div>
            )}

            {/* Upgrade Message - adapté selon le plan */}
            <p style={{ fontSize: '14px', color: '#999', marginBottom: '20px' }}>
              {planType === 'gold' || planType === 'enterprise'
                ? 'Revenez demain pour continuer à utiliser voxia.'
                : planType === 'silver'
                  ? 'Passez au plan Gold pour augmenter vos limites quotidiennes.'
                  : 'Passez au plan Silver pour augmenter vos limites quotidiennes.'}
            </p>

            {/* Buttons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Subscription Management Button */}
              <button
                onClick={() => openExternalUrl('https://voxia-md.ch/#/settings/subscription')}
                style={{
                  backgroundColor: 'var(--color-primary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '12px 24px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s ease'
                }}
                onMouseOver={(e) => e.target.style.opacity = '0.9'}
                onMouseOut={(e) => e.target.style.opacity = '1'}
              >
                Gérer mon abonnement
              </button>

              {/* Refresh Button */}
              <button
                onClick={() => checkUsageBeforeRecord()}
                style={{
                  backgroundColor: 'transparent',
                  color: 'var(--color-primary)',
                  border: '2px solid var(--color-primary)',
                  borderRadius: '10px',
                  padding: '10px 24px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = 'var(--color-primary)';
                  e.target.style.color = 'white';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = 'var(--color-primary)';
                }}
              >
                Rafraîchir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Annex Panel Overlay */}
      {annexOpen && (
        <div
          className="overlay-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setAnnexOpen(false);
            }
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10010,
            WebkitAppRegion: 'no-drag',
            padding: '20px'
          }}
        >
          <AnnexPanel
              annexText={annexText}
              setAnnexText={setAnnexText}
              onPaste={handlePasteAnnex}
              onClearAnnex={handleClearAnnex}
              onTakePhoto={handleTakePhoto}
              onSelectFromLibrary={handleSelectFromLibrary}
              fileInputRef={fileInputRef}
              onFileChange={onFileChange}
              onClose={() => setAnnexOpen(false)}
              isExtractingText={isExtractingText}
              ocrQueueLength={ocrQueueLength}
              onClearOcrQueue={clearOcrQueue}
              processingProgress={processingProgress}
              onScreenshotCapture={(capturedDataUrl) => {
                if (capturedDataUrl) {
                  // Convert base64 dataUrl to a File object
                  const byteString = atob(capturedDataUrl.split(',')[1]);
                  const mimeString = capturedDataUrl.split(',')[0].split(':')[1].split(';')[0];
                  const ab = new ArrayBuffer(byteString.length);
                  const ia = new Uint8Array(ab);
                  for (let i = 0; i < byteString.length; i++) {
                    ia[i] = byteString.charCodeAt(i);
                  }
                  const blob = new Blob([ab], { type: mimeString });
                  const file = new File([blob], `screenshot-${Date.now()}.png`, { type: mimeString });
                  addFileToOcrQueue(file);
                }
              }}
          />
        </div>
      )}

      {/* Queue Panel Overlay */}
      {showQueuePanel && (
        <div
          className="overlay-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowQueuePanel(false);
            }
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div
            className="queue-panel-card"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '40px',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
              textAlign: 'center'
            }}
          >

            {/* Audio library list */}
            <div style={{ textAlign: 'left', maxHeight: '50vh', overflowY: 'auto', marginBottom: 16 }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Segments enregistrés</div>
              {audioLibrary && audioLibrary.length > 0 ? (
                audioLibrary.map((item, idx) => (
                  <div key={item.id} style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    padding: '10px 12px',
                    border: '1px solid #eee',
                    borderRadius: 10,
                    marginBottom: 8,
                    background: '#fafafa'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {/* Status pill */}
                      <span style={{
                        display: 'inline-block',
                        padding: '3px 8px',
                        borderRadius: 999,
                        fontSize: 12,
                        color: item.status === 'done' ? '#0f5132' : item.status === 'error' ? '#842029' : item.status === 'processing' ? '#084298' : '#664d03',
                        backgroundColor: item.status === 'done' ? '#d1e7dd' : item.status === 'error' ? '#f8d7da' : item.status === 'processing' ? '#cfe2ff' : '#fff3cd',
                        border: '1px solid rgba(0,0,0,0.06)'
                      }}>
                        {item.status === 'done' && 'Transcrit'}
                        {item.status === 'error' && 'Erreur'}
                        {item.status === 'processing' && 'En cours'}
                        {item.status === 'pending' && 'En attente'}
                      </span>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <div style={{ fontSize: 13, color: '#333' }}>#{idx + 1}</div>
                        <div style={{ fontSize: 12, color: '#777' }}>
                          {item.durationSeconds ? `${item.durationSeconds}s • ` : ''}
                          {new Date(item.createdAt || item.timestamp || Date.now()).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                        {item.errorMessage && (
                          <div style={{ fontSize: 12, color: '#b42318', marginTop: 2 }}>{item.errorMessage}</div>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Button
                        variant="primary"
                        icon={FaRedo}
                        onClick={() => retryTranscription(item.id)}
                        disabled={isTranscribing}
                      >
                        
                      </Button>
                      <Button
                        variant="danger"
                        icon={FaTrash}
                        onClick={() => deleteAudioFromLibrary(item.id)}
                      >
                      </Button>
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ fontSize: 13, color: '#777' }}>Aucun segment enregistré.</div>
              )}
            </div>

            <Button 
              icon={FaTimes} 
              variant="secondary" 
              onClick={() => setShowQueuePanel(false)}
            >
            </Button>
          </div>
        </div>
      )}

      {/* FullScreen Editor for Capacitor */}
      {isFullScreenEnabled && (
        <FullScreenTextEditor {...fullScreenEditorProps} />
      )}
      {isTranscriptFullScreenEnabled && (
        isCapacitor && (
          <FullScreenTextEditor {...transcriptFullScreenEditorProps} />
        )
      )}
    </div>
  );
}
